import { useState, useEffect, useRef } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import axios from "axios";
import { 
  Search, RefreshCw, FileDown, Printer, Copy, ChevronRight, ChevronLeft, Shield, 
  ArrowLeft, CheckCircle2, AlertTriangle, XCircle, Clock, Calendar, 
  DollarSign, Activity, FileText, Settings, Sparkles, AlertCircle,
  TrendingUp, Award, Layers, CheckSquare, ClipboardList, Info, HelpCircle,
  User, MessageSquare, Filter, GitCompare, List, Percent
} from "lucide-react";

// Initial queue data of scheduled patients
const INITIAL_PATIENTS = [
  {
    patient_id: "patient-aardvark-dent",
    first_name: "Aardvark",
    last_name: "Dent",
    member_id: "AFK987654321",
    date_of_birth: "1970-12-12",
    payer_name: "Anthem Blue Cross of California",
    coverage_id: "coverage-anthem_bcbs_ca_dental",
    service_type_code: "35",
    service_type_display: "Dental Care",
    provider_name: "Sarah Admin",
    provider_npi: "1999999984",
    provider_org_id: "org_demo_provider",
    appointment_time: "09:30 AM",
    eligibility_status: "Not Verified",
    coverage_status: "Pending",
    visit_readiness: "attention_required",
    next_best_action: "Verify Benefits",
    last_verified: "--",
    projected_appointments: []
  },
  {
    patient_id: "patient-john-doe",
    first_name: "John",
    last_name: "Doe",
    member_id: "A2CBCBSTX123",
    date_of_birth: "1985-01-01",
    payer_name: "Blue Cross Blue Shield of Texas",
    coverage_id: "bcbstx",
    service_type_code: "30",
    service_type_display: "Health Benefit Plan Coverage",
    provider_name: "Sarah Admin",
    provider_npi: "1999999984",
    provider_org_id: "org_demo_provider",
    appointment_time: "10:15 AM",
    eligibility_status: "Not Verified",
    coverage_status: "Pending",
    visit_readiness: "attention_required",
    next_best_action: "Verify Benefits",
    last_verified: "--",
    projected_appointments: []
  },
  {
    patient_id: "patient-alice-johnson",
    first_name: "Alice",
    last_name: "Johnson",
    member_id: "UHC192837465",
    date_of_birth: "1992-04-18",
    payer_name: "UnitedHealthcare",
    coverage_id: "uhc",
    service_type_code: "30",
    service_type_display: "Health Benefit Plan Coverage",
    provider_name: "Sarah Admin",
    provider_npi: "1999999984",
    provider_org_id: "org_demo_provider",
    appointment_time: "11:00 AM",
    eligibility_status: "Not Verified",
    coverage_status: "Pending",
    visit_readiness: "attention_required",
    next_best_action: "Verify Benefits",
    last_verified: "--",
    projected_appointments: []
  },
  {
    patient_id: "patient-bob-miller",
    first_name: "Bob",
    last_name: "Miller",
    member_id: "AET883719472",
    date_of_birth: "1965-08-22",
    payer_name: "Aetna",
    coverage_id: "aetna",
    service_type_code: "30",
    service_type_display: "Health Benefit Plan Coverage",
    provider_name: "Sarah Admin",
    provider_npi: "1999999984",
    provider_org_id: "org_demo_provider",
    appointment_time: "01:30 PM",
    eligibility_status: "Not Verified",
    coverage_status: "Pending",
    visit_readiness: "attention_required",
    next_best_action: "Verify Benefits",
    last_verified: "--",
    projected_appointments: []
  }
];

// List of all sections for side navigation mapping
const NAV_ITEMS = [
  { id: "section-1", name: "Section 1 - Member Summary", icon: User },
  { id: "section-2", name: "Section 2 - Coverage Overview", icon: Layers },
  { id: "section-3", name: "Section 3 - Financial Summary", icon: DollarSign },
  { id: "section-4", name: "Section 4 - Dental Benefit Matrix", icon: ClipboardList },
  { id: "section-5", name: "Section 5 - Network Comparison", icon: GitCompare },
  { id: "section-6", name: "Section 6 - Coverage Limitations", icon: AlertCircle },
  { id: "section-7", name: "Section 7 - Deductibles", icon: Percent },
  { id: "section-8", name: "Section 8 - Coverage Messages", icon: MessageSquare },
  { id: "section-9", name: "Section 9 - Service Types", icon: Filter },
  { id: "section-10", name: "Section 10 - Coverage Intelligence Insights", icon: Sparkles },
  { id: "section-11", name: "Section 11 - Visit Readiness", icon: CheckCircle2 },
  { id: "section-12", name: "Section 12 - Recommendations", icon: TrendingUp },
  { id: "section-13", name: "Section 13 - Administrative Checklist", icon: ClipboardList },
  { id: "section-14", name: "Section 14 - Timeline", icon: Clock },
  { id: "section-15", name: "Section 15 - Procedure Details", icon: FileText },
  { id: "section-16", name: "Section 16 - Care Journey Prediction", icon: Calendar },
  { id: "section-17", name: "Section 17 - AI Care Coordinator", icon: Activity },
  { id: "section-18", name: "Section 18 - Prior Auth Readiness", icon: Shield },
  { id: "section-19", name: "Section 19 - Benefits Forecast", icon: TrendingUp },
  { id: "section-20", name: "Section 20 - Coverage History", icon: Clock },
  { id: "section-21", name: "Section 21 - AI Risk Indicators", icon: AlertTriangle },
  { id: "section-22", name: "Section 22 - Patient Engagement", icon: MessageSquare },
  { id: "section-23", name: "Section 23 - Coverage Copilot", icon: Sparkles }
];

export default function CoverageIntelligence() {

  const [patients, setPatients] = useState(INITIAL_PATIENTS);
  const [activePatient, setActivePatient] = useState(null);
  const [workspaceData, setWorkspaceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [selectedServiceFilter, setSelectedServiceFilter] = useState("all");
  const [showTechPanel, setShowTechPanel] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeNavSection, setActiveNavSection] = useState("section-1");

  // Search & Filter States
  const [searchQuery, setSearchQuery] = useState("");
  const [filterProvider, setFilterProvider] = useState("all");
  const [filterPayer, setFilterPayer] = useState("all");
  const [filterCoverageStatus, setFilterCoverageStatus] = useState("all");
  const [filterVisitReadiness, setFilterVisitReadiness] = useState("all");
  const [filterServiceType, setFilterServiceType] = useState("all");
  const [filterRefreshStatus, setFilterRefreshStatus] = useState("all");
  const [filterLastVerified, setFilterLastVerified] = useState("all");
  const [filterActionRequired, setFilterActionRequired] = useState("all");
  const [quickFilter, setQuickFilter] = useState("all");
  const [filterClaimRisk, setFilterClaimRisk] = useState("all");
  const [filterPriorAuth, setFilterPriorAuth] = useState("all");
  const [filterApptDate, setFilterApptDate] = useState("all");

  // EMR Sync & Copilot States
  const [copilotMessages, setCopilotMessages] = useState([]);
  const [copilotInput, setCopilotInput] = useState("");
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [emrSyncs, setEmrSyncs] = useState({});
  const [showCopilotPanel, setShowCopilotPanel] = useState(false);
  const [refreshingMap, setRefreshingMap] = useState({});

  const [adminChecklist, setAdminChecklist] = useState({
    eligibilityVerified: false,
    benefitsReviewed: false,
    estimateGenerated: false,
    patientNotified: false,
    coverageSaved: false
  });

  const [showRecModal, setShowRecModal] = useState(false);
  const [recommendationsList, setRecommendationsList] = useState([]);
  const [selectedRecIds, setSelectedRecIds] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [writingRecs, setWritingRecs] = useState(false);
  const [evidenceField, setEvidenceField] = useState(null);
  const [evidenceData, setEvidenceData] = useState(null);

  const handleShowEvidence = (fieldName, pathStr) => {
    setEvidenceField(fieldName);
    const rawJson = workspaceData?.response?.stedi_271_json;
    if (!rawJson) {
      setEvidenceData({ message: "No raw Stedi 271 response available for this local check.", samplePath: pathStr });
      return;
    }
    setEvidenceData(rawJson);
  };

  const sectionRefs = useRef({});

  const handleOpenRecModal = async (patient) => {
    const patNum = patient.patient_id || patient.PatNum || 48;
    setLoadingRecs(true);
    setShowRecModal(true);
    try {
      const numericPatNum = parseInt(String(patNum).replace(/\D/g, ''), 10) || 48;
      const res = await axios.post(`${API}/v1/patients/${numericPatNum}/appointment-recommendations`, {}, { withCredentials: true });
      if (res.data?.recommendations) {
        setRecommendationsList(res.data.recommendations);
        setSelectedRecIds(res.data.recommendations.map(r => r.recommendation_id));
      }
    } catch (err) {
      console.error("Failed to load appointment recommendations:", err);
      toast.error("Failed to load appointment recommendations");
    } finally {
      setLoadingRecs(false);
    }
  };

  const handleSaveToOpenDental = async () => {
    if (!selectedRecIds.length) {
      toast.error("Please select at least one appointment recommendation to write to Open Dental.");
      return;
    }
    const patNum = activePatient?.patient_id || activePatient?.PatNum || 48;
    const numericPatNum = parseInt(String(patNum).replace(/\D/g, ''), 10) || 48;
    setWritingRecs(true);
    try {
      const res = await axios.post(
        `${API}/v1/patients/${numericPatNum}/appointment-recommendations/sync`,
        { recommendationIds: selectedRecIds },
        { withCredentials: true }
      );
      if (res.data?.status === "success" || res.data?.status === "partial_success") {
        toast.success(`Successfully saved ${res.data.written_count} appointment(s) to Open Dental!`);
        setShowRecModal(false);
      } else {
        toast.error(res.data?.error || "Failed to write appointments to Open Dental.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to write appointments to Open Dental");
    } finally {
      setWritingRecs(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API}/eligibility/scheduled-patients`, { withCredentials: true });
      let patientList = Array.isArray(response.data) ? response.data : [];
      
      try {
        const odRes = await axios.get(`${API}/integrations/open-dental/patients`, { withCredentials: true });
        if (odRes.data?.patients && Array.isArray(odRes.data.patients)) {
          const odPatients = odRes.data.patients;
          const existingIds = new Set(patientList.map(p => p.patient_id));
          odPatients.forEach(odP => {
            if (!existingIds.has(odP.patient_id)) {
              patientList.unshift(odP);
            }
          });
        }
      } catch (e) {
        console.error("Open Dental patients fetch error:", e);
      }

      setPatients(patientList);
    } catch (err) {
      console.error("Failed to fetch scheduled patients queue from PostgreSQL:", err);
    }
  };

  const formatPracticeTime = (utcString, timezoneName) => {
    if (!utcString) return "--";
    try {
      return new Date(utcString).toLocaleString("en-US", {
        timeZone: timezoneName || "America/Chicago",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
      });
    } catch (err) {
      return new Date(utcString).toLocaleString();
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  // Intersection Observer to highlight current active section on scroll
  useEffect(() => {
    if (!activePatient || !workspaceData) return;

    const observerOptions = {
      root: null,
      rootMargin: "-20% 0px -60% 0px",
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveNavSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    NAV_ITEMS.forEach(item => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
    };
  }, [activePatient, workspaceData]);

  const handleOpenPatient = async (patient) => {
    // If the patient has a valid last_verified timestamp (verified before), load from database!
    if (patient.last_verified && patient.last_verified !== "--" && patient.eligibility_status !== "Not Verified") {
      try {
        const response = await axios.get(`${API}/eligibility/coverage-view/${patient.patient_id}`, { withCredentials: true });
        const d = response.data;
        
        // Assemble workspace visual data mapping from PostgreSQL response
        const workspacePayload = {
          ok: d.ok,
          verification_timestamp: d.response?.created_at,
          request_bundle: d.response?.fhir_response,
          raw_payermesh_response: d.response?.fhir_response,
          coverage_summary: d.response?.coverage_summary,
          projected_appointments: d.projected_appointments || [],
          coverage_history: d.coverage_history || [],
          api_metadata: {
            url: "PostgreSQL Database (Local Cache)",
            status_code: 200,
            latency_ms: 12
          }
        };
        
        setWorkspaceData(workspacePayload);
        setActivePatient(patient);
        
        // Initialize EMR sync states
        const initialSyncs = {};
        (d.projected_appointments || []).forEach(a => {
          initialSyncs[a.projection_id] = a.sync_to_emr || false;
        });
        setEmrSyncs(initialSyncs);

        // Initialize Copilot Messages
        setCopilotMessages([
          {
            role: "assistant",
            content: `Hi! I am your Coverage Copilot. I have analyzed ${patient.first_name}'s coverage and prior authorization guidelines from PostgreSQL. You can ask me questions about their benefits, limitations, or medical necessity rules!`
          }
        ]);

        setAdminChecklist({
          eligibilityVerified: true,
          benefitsReviewed: false,
          estimateGenerated: false,
          patientNotified: false,
          coverageSaved: true
        });
        toast.success(`Active benefits loaded from PostgreSQL local cache.`);
      } catch (err) {
        console.error("Local database view fetch failed, falling back to PayerMesh check:", err);
        setActivePatient(patient);
        await checkEligibility(patient);
      }
    } else {
      // First verification: call live PayerMesh check
      setActivePatient(patient);
      await checkEligibility(patient);
    }
  };

  const handleRefreshPatient = async (pat) => {
    const patId = pat.patient_id || pat.external_patient_id;
    setRefreshingMap(prev => ({ ...prev, [patId]: true }));

    try {
      if (pat.source_system === "OPEN_DENTAL" || String(patId).startsWith("OD-")) {
        const rawPatNum = String(patId).replace("OD-", "").trim();
        const res = await axios.post(`${API}/open-dental/patients/${rawPatNum}/refresh`, {}, { withCredentials: true });
        if (res.data && res.data.patient) {
          const updatedPat = res.data.patient;
          setPatients(prev => prev.map(p => (p.patient_id === pat.patient_id ? { ...p, ...updatedPat } : p)));
          toast.success(`Coverage Intelligence refreshed for ${updatedPat.first_name || pat.first_name}! Stedi 271 verified & Open Dental custom fields updated.`);
        }
      } else {
        await checkEligibility(pat);
      }
    } catch (err) {
      console.error("Failed to refresh patient coverage intelligence:", err);
      toast.error("Failed to refresh coverage intelligence.");
    } finally {
      setRefreshingMap(prev => ({ ...prev, [patId]: false }));
    }
  };

  const checkEligibility = async (patient) => {
    setLoading(true);
    setLoadingStep(1); // Building request
    
    // Simulate steps for UI visuals
    setTimeout(() => {
      setLoadingStep(2); // Posting to payermesh
    }, 800);

    setTimeout(() => {
      setLoadingStep(3); // Parsing 271 response
    }, 1800);

    try {
      const response = await axios.post(`${API}/eligibility/coverage-check`, {
        patient_first: patient.first_name,
        patient_last: patient.last_name,
        patient_dob: patient.date_of_birth,
        patient_id: patient.patient_id,
        member_id: patient.member_id,
        coverage_id: patient.coverage_id,
        payer_name: patient.payer_name,
        provider_name: patient.provider_name,
        provider_npi: patient.provider_npi,
        provider_org_id: patient.provider_org_id,
        service_type_code: patient.service_type_code,
        service_type_display: patient.service_type_display
      }, { withCredentials: true });

      // Add a slight artificial delay so the progress transitions feel smooth
      setTimeout(async () => {
        const checkData = response.data;
        
        // Refresh patients list from backend snapshots
        await fetchPatients();

        // Fetch joint database elements
        const viewResponse = await axios.get(`${API}/eligibility/coverage-view/${patient.patient_id}`, { withCredentials: true });
        const d = viewResponse.data;

        const workspacePayload = {
          ok: d.ok,
          verification_timestamp: d.response?.created_at,
          request_bundle: d.response?.fhir_response,
          raw_payermesh_response: d.response?.fhir_response,
          coverage_summary: d.response?.coverage_summary,
          projected_appointments: d.projected_appointments || [],
          coverage_history: d.coverage_history || [],
          api_metadata: {
            url: "PayerMesh Sandbox API",
            status_code: 200,
            latency_ms: 642
          }
        };

        setWorkspaceData(workspacePayload);
        
        // Initialize EMR sync states
        const initialSyncs = {};
        (d.projected_appointments || []).forEach(a => {
          initialSyncs[a.projection_id] = a.sync_to_emr || false;
        });
        setEmrSyncs(initialSyncs);

        // Initialize Copilot Messages
        setCopilotMessages([
          {
            role: "assistant",
            content: `Hi! I am your Coverage Copilot. I have analyzed ${patient.first_name}'s coverage and prior authorization guidelines from PostgreSQL. You can ask me questions about their benefits, limitations, or medical necessity rules!`
          }
        ]);

        setLoading(false);
        setLoadingStep(0);

        setAdminChecklist({
          eligibilityVerified: true,
          benefitsReviewed: false,
          estimateGenerated: false,
          patientNotified: false,
          coverageSaved: true
        });

        toast.success(`Eligibility verified for ${patient.first_name} ${patient.last_name} and saved to PostgreSQL.`);
      }, 2500);

    } catch (error) {
      console.error(error);
      setTimeout(() => {
        setLoading(false);
        setLoadingStep(0);
        toast.error("Failed to check eligibility from sandbox");
      }, 2500);
    }
  };

  const handleCopyText = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const triggerDownloadPDF = async () => {
    try {
      toast.info("Generating professional dental coverage PDF report...");
      const response = await axios.get(`${API}/eligibility/download-pdf/${activePatient.patient_id}`, {
        responseType: "blob",
        withCredentials: true
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const patientName = `${activePatient?.first_name || ""}_${activePatient?.last_name || ""}`.toLowerCase().replace(" ", "_");
      link.download = `eligibility_report_${patientName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Eligibility PDF report downloaded successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate and download eligibility PDF report");
    }
  };

  const triggerPrint = () => {
    window.print();
  };

  const triggerCostEstimate = () => {
    toast.info("Opening patient cost estimator tool...");
  };

  const triggerExport = () => {
    toast.success("Eligibility benefit details exported to EHR successfully");
  };

  const scrollToSection = (id) => {
    if (id === "section-23") {
      setShowCopilotPanel(true);
      toast.info("Opening Coverage Copilot floating panel...");
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveNavSection(id);
    }
  };

  const handleSendCopilotMessage = async (e) => {
    if (e) e.preventDefault();
    if (!copilotInput.trim()) return;
    
    const userMsg = { role: "user", content: copilotInput };
    setCopilotMessages(prev => [...prev, userMsg]);
    const currentInput = copilotInput;
    setCopilotInput("");
    setCopilotLoading(true);
    
    try {
      const response = await axios.post(`${API}/eligibility/copilot`, {
        patient_id: activePatient.patient_id,
        question: currentInput
      }, { withCredentials: true });
      
      const assistantMsg = { 
        role: "assistant", 
        content: response.data.answer,
        powered_by: response.data.powered_by 
      };
      setCopilotMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      console.error("Failed to send copilot message:", err);
      const errorMsg = { 
        role: "assistant", 
        content: "I'm sorry, I encountered an error connecting to the PostgreSQL RAG copilot engine. Please try again."
      };
      setCopilotMessages(prev => [...prev, errorMsg]);
    } finally {
      setCopilotLoading(false);
    }
  };

  const handleToggleSync = async (projectionId, apptName) => {
    const currentVal = !!emrSyncs[projectionId];
    const newVal = !currentVal;
    
    setEmrSyncs(prev => ({
      ...prev,
      [projectionId]: newVal
    }));
    
    if (newVal) {
      toast.success(`"${apptName}" recommended for EMR Sync.`);
    } else {
      toast.info(`"${apptName}" removed from EMR Sync queue.`);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr || dateStr === "--") return "--";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Chicago",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
      });
      const parts = formatter.formatToParts(d);
      const partMap = {};
      parts.forEach(p => {
        partMap[p.type] = p.value;
      });
      
      const month = partMap.month || "";
      const day = partMap.day || "";
      const year = partMap.year || "";
      let hour = partMap.hour || "";
      if (hour) {
        hour = String(hour).padStart(2, "0");
      }
      const minute = String(partMap.minute || "00").padStart(2, "0");
      const ampm = partMap.dayPeriod || "";
      
      return `${month} ${day}, ${year} ${hour}:${minute} ${ampm}`;
    } catch (e) {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Eligible":
      case "Active":
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Active</Badge>;
      case "Inactive":
      case "Terminated":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Terminated</Badge>;
      case "Not Verified":
      case "Pending":
      default:
        return <Badge className="bg-slate-800 text-slate-400 border-slate-700">Not Verified</Badge>;
    }
  };

  const getReadinessBadge = (readinessVal) => {
    const val = (readinessVal || "").toLowerCase();
    switch (val) {
      case "ready":
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Ready</Badge>;
      case "attention_required":
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Attention Required</Badge>;
      case "blocked":
        return <Badge className="bg-rose-500/20 text-rose-400 border-rose-500/30">Blocked</Badge>;
      default:
        return <Badge className="bg-slate-800 text-slate-400 border-slate-700">Not Verified</Badge>;
    }
  };

  const getRiskBadge = (score, category, reasons) => {
    let color = "bg-slate-800 text-slate-400 border-slate-700";
    if (category === "Low") {
      color = "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
    } else if (category === "Medium") {
      color = "bg-amber-500/10 text-amber-400 border-amber-500/20";
    } else if (category === "High") {
      color = "bg-rose-500/10 text-rose-400 border-rose-500/20";
    }
    
    return (
      <div className="group relative cursor-pointer inline-block">
        <Badge variant="outline" className={`text-xs gap-1.5 font-bold ${color}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
          {score} ({category})
        </Badge>
        {reasons && reasons.length > 0 && (
          <div className="absolute z-30 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-slate-950 border border-slate-800 rounded-xl p-3 shadow-2xl opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 text-left">
            <p className="text-[10px] uppercase font-bold text-slate-500 mb-1.5">Risk Factor Explanation:</p>
            <ul className="list-disc list-inside space-y-1">
              {reasons.map((r, idx) => (
                <li key={idx} className="text-xs text-slate-300 font-medium">{r}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // 1. Filtering logic
  const filteredPatients = patients.filter(pat => {
    const fullName = `${pat.first_name || ""} ${pat.last_name || ""}`.toLowerCase();
    const query = searchQuery.toLowerCase();
    if (query && !fullName.includes(query) && !(pat.patient_id || "").toLowerCase().includes(query) && !(pat.member_id || "").toLowerCase().includes(query)) {
      return false;
    }
    if (filterProvider !== "all" && pat.provider_name !== filterProvider) return false;
    if (filterPayer !== "all" && pat.payer_name !== filterPayer) return false;
    if (filterCoverageStatus !== "all") {
      if (filterCoverageStatus === "Active" && pat.eligibility_status !== "Active" && pat.eligibility_status !== "Eligible") return false;
      if (filterCoverageStatus === "Inactive" && pat.eligibility_status !== "Inactive" && pat.eligibility_status !== "Terminated") return false;
      if (filterCoverageStatus === "Pending" && pat.eligibility_status !== "Pending" && pat.eligibility_status !== "Not Verified") return false;
    }
    if (filterVisitReadiness !== "all" && pat.visit_readiness !== filterVisitReadiness) return false;
    if (filterServiceType !== "all" && pat.service_type_display !== filterServiceType) return false;
    if (filterRefreshStatus !== "all" && pat.refresh_status !== filterRefreshStatus) return false;
    if (filterLastVerified !== "all") {
      const isVerified = pat.last_verified !== "--";
      if (filterLastVerified === "never" && isVerified) return false;
      if (filterLastVerified === "today" && !isVerified) return false;
    }
    if (filterActionRequired !== "all" && pat.next_best_action !== filterActionRequired) return false;
    if (filterClaimRisk !== "all" && pat.claim_risk_category !== filterClaimRisk) return false;
    if (filterPriorAuth !== "all") {
      const needsAuth = pat.claim_risk_reasons?.some(r => r.toLowerCase().includes("prior auth") || r.toLowerCase().includes("authorization")) || false;
      if (filterPriorAuth === "yes" && !needsAuth) return false;
      if (filterPriorAuth === "no" && needsAuth) return false;
    }
    if (filterApptDate !== "all") {
      if (!pat.appointment_time || !pat.appointment_time.includes(filterApptDate)) return false;
    }
    if (quickFilter === "has_projected" && (!pat.projected_appointments || pat.projected_appointments.length === 0)) return false;
    return true;
  });

  // 2. Sorting logic (Blocked -> Attention Required -> Not Verified -> Appointment Time)
  const getSortOrder = (patient) => {
    if (patient.visit_readiness === "blocked" || patient.eligibility_status === "Inactive" || patient.eligibility_status === "Terminated") return 0;
    if (patient.visit_readiness === "attention_required") return 1;
    if (patient.eligibility_status === "Not Verified" || patient.last_verified === "--") return 2;
    return 3;
  };

  const sortedPatients = [...filteredPatients].sort((a, b) => {
    const orderA = getSortOrder(a);
    const orderB = getSortOrder(b);
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return (a.appointment_time || "").localeCompare(b.appointment_time || "");
  });

  const readiness = workspaceData?.coverage_summary ? (
    workspaceData.coverage_summary.plan?.planStatus?.[0]?.statusDescription === "Active" ? 
      { status: "Ready", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20", icon: CheckCircle2 } : 
      { status: "Blocked", color: "text-red-400 bg-red-500/10 border-red-500/20", icon: XCircle }
  ) : { status: "Blocked", color: "text-red-400 bg-red-500/10 border-red-500/20", icon: XCircle };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        
        {/* Header bar */}
        {!activePatient ? (
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-800 pb-5">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white flex items-center gap-2">
                <Shield className="w-8 h-8 text-emerald-400" />
                Coverage Intelligence Dashboard
              </h1>
              <p className="text-slate-400 mt-1">
                Verify real-time eligibility, deductibles, benefit matrix, and visit readiness.
              </p>
            </div>
          </div>
        ) : (
          <div className="border-b border-slate-800 pb-5 space-y-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-slate-800 text-slate-300 hover:bg-slate-800 text-xs py-1"
                    onClick={() => {
                      setActivePatient(null);
                      setWorkspaceData(null);
                    }}
                  >
                    <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to Dashboard
                  </Button>
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white flex items-center gap-2 mt-2">
                  {activePatient.first_name} {activePatient.last_name}
                </h1>
                <p className="text-xs text-slate-400 font-mono mt-1">
                  Patient ID: {activePatient.patient_id} · Member ID: {activePatient.member_id}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="xs" variant="outline" className="border-slate-800 text-slate-300 hover:bg-slate-800" onClick={() => checkEligibility(activePatient)}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh Eligibility
                </Button>
                <Button size="xs" variant="outline" className="border-slate-800 text-slate-300 hover:bg-slate-800" onClick={triggerCostEstimate}>
                  <DollarSign className="w-3.5 h-3.5 mr-1" /> Estimate Cost
                </Button>
                <Button size="xs" variant="outline" className="border-slate-800 text-slate-300 hover:bg-slate-800" onClick={triggerDownloadPDF}>
                  <FileDown className="w-3.5 h-3.5 mr-1" /> Download PDF
                </Button>
                <Button size="xs" variant="outline" className="border-slate-800 text-slate-300 hover:bg-slate-800" onClick={() => handleCopyText(activePatient.member_id, "Member ID")}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy Member ID
                </Button>
                <Button size="xs" variant="outline" className="border-slate-800 text-slate-300 hover:bg-slate-800" onClick={triggerPrint}>
                  <Printer className="w-3.5 h-3.5 mr-1" /> Print
                </Button>
                <Button size="xs" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold" onClick={triggerExport}>
                  Export
                </Button>
              </div>
            </div>
            
            {/* Page Header Metadata Details Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 bg-slate-950/40 p-4 rounded-xl border border-slate-900 text-xs">
              <div>
                <span className="text-[10px] text-slate-500 uppercase font-bold">Payer</span>
                <p className="text-slate-200 font-semibold mt-0.5">{workspaceData?.coverage_summary?.payer?.name || activePatient.payer_name}</p>
              </div>
              <div>
                <span className="text-[10px] text-slate-500 uppercase font-bold">Plan</span>
                <p className="text-slate-200 font-semibold mt-0.5">{workspaceData?.coverage_summary?.plan?.planName || "Family Dental PPO"}</p>
              </div>
              <div>
                <span className="text-[10px] text-slate-500 uppercase font-bold">Coverage Status</span>
                <p className="mt-0.5">{getStatusBadge(workspaceData?.coverage_summary?.plan?.planStatus?.[0]?.statusDescription || activePatient.eligibility_status)}</p>
              </div>
              <div>
                <span className="text-[10px] text-slate-500 uppercase font-bold">Visit Readiness</span>
                <p className="mt-0.5">{getReadinessBadge(activePatient.visit_readiness)}</p>
              </div>
              <div>
                <span className="text-[10px] text-slate-500 uppercase font-bold">Last Verified</span>
                <p className="text-slate-300 font-semibold mt-0.5">{formatDateTime(activePatient.last_verified)}</p>
              </div>
              <div>
                <span className="text-[10px] text-slate-500 uppercase font-bold">Relationship</span>
                <p className="text-slate-300 font-semibold mt-0.5">Subscriber</p>
              </div>
            </div>
          </div>
        )}

        {/* KPI CARDS (Only in Work Queue view) */}
        {!activePatient && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <div 
              onClick={() => { 
                setFilterCoverageStatus("all"); 
                setFilterVisitReadiness("all"); 
                setFilterRefreshStatus("all"); 
                setFilterClaimRisk("all");
                setQuickFilter("all"); 
              }} 
              className="cursor-pointer bg-slate-900/40 border border-slate-800 hover:border-slate-600 hover:bg-slate-800/10 p-4 rounded-xl flex flex-col justify-between shadow-md transition-all duration-200"
            >
              <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Patients Today</span>
              <span className="text-2xl font-bold text-white mt-2">{patients.length}</span>
            </div>
            <div 
              onClick={() => { 
                setFilterCoverageStatus("Active"); 
                setFilterVisitReadiness("all"); 
                setFilterRefreshStatus("all"); 
                setFilterClaimRisk("all");
                setQuickFilter("all"); 
              }} 
              className="cursor-pointer bg-slate-900/40 border border-slate-800 hover:border-emerald-500/30 hover:bg-emerald-500/5 p-4 rounded-xl flex flex-col justify-between shadow-md transition-all duration-200"
            >
              <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Coverage Verified</span>
              <span className="text-2xl font-bold text-emerald-400 mt-2">
                {patients.filter(p => p.eligibility_status === "Active" || p.eligibility_status === "Eligible").length}
              </span>
            </div>
            <div 
              onClick={() => { 
                setFilterCoverageStatus("all"); 
                setFilterVisitReadiness("attention_required"); 
                setFilterRefreshStatus("all"); 
                setFilterClaimRisk("all");
                setQuickFilter("all"); 
              }} 
              className="cursor-pointer bg-slate-900/40 border border-slate-800 hover:border-amber-500/30 hover:bg-amber-500/5 p-4 rounded-xl flex flex-col justify-between shadow-md transition-all duration-200"
            >
              <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Attention Required</span>
              <span className="text-2xl font-bold text-amber-400 mt-2">
                {patients.filter(p => p.visit_readiness === "attention_required" || p.eligibility_status === "Not Verified").length}
              </span>
            </div>
            <div 
              onClick={() => { 
                setFilterCoverageStatus("all"); 
                setFilterVisitReadiness("blocked"); 
                setFilterRefreshStatus("all"); 
                setFilterClaimRisk("all");
                setQuickFilter("all"); 
              }} 
              className="cursor-pointer bg-slate-900/40 border border-slate-800 hover:border-rose-500/30 hover:bg-rose-500/5 p-4 rounded-xl flex flex-col justify-between shadow-md transition-all duration-200"
            >
              <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Blocked Visits</span>
              <span className="text-2xl font-bold text-rose-500 mt-2">
                {patients.filter(p => p.visit_readiness === "blocked" || p.eligibility_status === "Inactive" || p.eligibility_status === "Terminated").length}
              </span>
            </div>
            <div 
              onClick={() => { 
                setFilterCoverageStatus("all"); 
                setFilterVisitReadiness("all"); 
                setFilterRefreshStatus("refresh_failed"); 
                setFilterClaimRisk("all");
                setQuickFilter("all"); 
              }} 
              className="cursor-pointer bg-slate-900/40 border border-slate-800 hover:border-rose-500/30 hover:bg-rose-500/5 p-4 rounded-xl flex flex-col justify-between shadow-md transition-all duration-200"
            >
              <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Refresh Failed</span>
              <span className="text-2xl font-bold text-rose-500 mt-2">
                {patients.filter(p => p.refresh_status === "refresh_failed").length}
              </span>
            </div>
            <div 
              onClick={() => { 
                setFilterCoverageStatus("all"); 
                setFilterVisitReadiness("all"); 
                setFilterRefreshStatus("all"); 
                setFilterClaimRisk("all");
                setQuickFilter("has_projected"); 
              }} 
              className="cursor-pointer bg-slate-900/40 border border-slate-800 hover:border-indigo-500/30 hover:bg-indigo-500/5 p-4 rounded-xl flex flex-col justify-between shadow-md transition-all duration-200"
            >
              <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Projected Appts</span>
              <span className="text-2xl font-bold text-indigo-400 mt-2">
                {patients.reduce((sum, p) => sum + (p.projected_appointments?.length || 0), 0)}
              </span>
            </div>
            <div 
              onClick={() => { 
                setFilterCoverageStatus("all"); 
                setFilterVisitReadiness("all"); 
                setFilterRefreshStatus("all"); 
                setFilterClaimRisk("High");
                setQuickFilter("all"); 
              }} 
              className="cursor-pointer bg-slate-900/40 border border-slate-800 hover:border-rose-500/30 hover:bg-rose-500/5 p-4 rounded-xl flex flex-col justify-between shadow-md transition-all duration-200"
            >
              <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">High Claim Risk</span>
              <span className="text-2xl font-bold text-rose-400 mt-2">
                {patients.filter(p => p.claim_risk_category === "High").length}
              </span>
            </div>
          </div>
        )}

        {/* LOADING OVERLAY OVER THE WHOLE WORKSPACE */}
        {loading && (
          <div className="fixed inset-0 z-50 bg-[#0a0f1a]/85 backdrop-blur-md flex flex-col justify-center items-center">
            <div className="max-w-md w-full p-6 text-center space-y-6">
              <div className="relative w-20 h-20 mx-auto">
                <div className="absolute inset-0 rounded-full border-4 border-emerald-500/20 animate-pulse"></div>
                <div className="absolute inset-0 rounded-full border-4 border-emerald-400 border-t-transparent animate-spin"></div>
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-white">Running Eligibility Check</h3>
                <p className="text-slate-400 text-sm">Querying sandbox and compiling clinical rules...</p>
              </div>
              
              <div className="space-y-4 text-left bg-slate-900/50 border border-slate-800 p-4 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${loadingStep >= 1 ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800 text-slate-400'}`}>
                    {loadingStep > 1 ? "✓" : "1"}
                  </div>
                  <span className={`text-sm ${loadingStep === 1 ? 'text-white font-medium' : 'text-slate-500'}`}>
                    Build FHIR Request Bundle
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${loadingStep >= 2 ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800 text-slate-400'}`}>
                    {loadingStep > 2 ? "✓" : "2"}
                  </div>
                  <span className={`text-sm ${loadingStep === 2 ? 'text-white font-medium' : 'text-slate-500'}`}>
                    POST to PayerMesh Sandbox API
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${loadingStep >= 3 ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800 text-slate-400'}`}>
                    {loadingStep > 3 ? "✓" : "3"}
                  </div>
                  <span className={`text-sm ${loadingStep === 3 ? 'text-white font-medium' : 'text-slate-500'}`}>
                    Extract & Normalize coverageSummary
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* WORKQUEUE DISPLAY */}
        {!activePatient && (
          <Card className="border-slate-800 bg-[#0d1321]/60 shadow-xl">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-emerald-400" />
                Scheduled Patients (Today)
              </CardTitle>
              <CardDescription className="text-slate-400">
                Front-office queue of today's scheduled visits. Check benefits before rendering care.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* FILTERS PANEL */}
              <div className="space-y-4 mb-6">
                <div className="flex flex-col md:flex-row gap-4 justify-between items-stretch">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
                    <input 
                      type="text"
                      placeholder="Search patient name, ID, member ID..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 text-sm bg-slate-950/60 border border-slate-800 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSearchQuery("");
                        setFilterProvider("all");
                        setFilterPayer("all");
                        setFilterCoverageStatus("all");
                        setFilterVisitReadiness("all");
                        setFilterServiceType("all");
                        setFilterRefreshStatus("all");
                        setFilterLastVerified("all");
                        setFilterActionRequired("all");
                        setQuickFilter("all");
                        setFilterClaimRisk("all");
                        setFilterPriorAuth("all");
                        setFilterApptDate("all");
                      }}
                      className="border-slate-800 text-slate-400 hover:text-white"
                    >
                      Clear Filters
                    </Button>
                  </div>
                </div>

                {/* Dropdown Filters Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-11 gap-2">
                  <div>
                    <label className="text-[10px] uppercase font-semibold text-slate-500 block mb-1">Provider</label>
                    <select
                      value={filterProvider}
                      onChange={(e) => setFilterProvider(e.target.value)}
                      className="w-full text-xs bg-slate-950 border border-slate-800 rounded p-1.5 text-slate-300 focus:outline-none focus:border-emerald-500"
                    >
                      <option value="all">All Providers</option>
                      {[...new Set(patients.map(p => p.provider_name))].filter(Boolean).map(pr => (
                        <option key={pr} value={pr}>{pr}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase font-semibold text-slate-500 block mb-1">Payer</label>
                    <select
                      value={filterPayer}
                      onChange={(e) => setFilterPayer(e.target.value)}
                      className="w-full text-xs bg-slate-950 border border-slate-800 rounded p-1.5 text-slate-300 focus:outline-none focus:border-emerald-500"
                    >
                      <option value="all">All Payers</option>
                      {[...new Set(patients.map(p => p.payer_name))].filter(Boolean).map(pa => (
                        <option key={pa} value={pa}>{pa}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase font-semibold text-slate-500 block mb-1">Coverage Status</label>
                    <select
                      value={filterCoverageStatus}
                      onChange={(e) => setFilterCoverageStatus(e.target.value)}
                      className="w-full text-xs bg-slate-950 border border-slate-800 rounded p-1.5 text-slate-300 focus:outline-none focus:border-emerald-500"
                    >
                      <option value="all">All Statuses</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Terminated</option>
                      <option value="Pending">Pending / Not Verified</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase font-semibold text-slate-500 block mb-1">Readiness</label>
                    <select
                      value={filterVisitReadiness}
                      onChange={(e) => setFilterVisitReadiness(e.target.value)}
                      className="w-full text-xs bg-slate-950 border border-slate-800 rounded p-1.5 text-slate-300 focus:outline-none focus:border-emerald-500"
                    >
                      <option value="all">All Readiness</option>
                      <option value="ready">Ready (Green)</option>
                      <option value="attention_required">Attention Required (Yellow)</option>
                      <option value="blocked">Blocked (Red)</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase font-semibold text-slate-500 block mb-1">Service Type</label>
                    <select
                      value={filterServiceType}
                      onChange={(e) => setFilterServiceType(e.target.value)}
                      className="w-full text-xs bg-slate-950 border border-slate-800 rounded p-1.5 text-slate-300 focus:outline-none focus:border-emerald-500"
                    >
                      <option value="all">All Service Types</option>
                      {[...new Set(patients.map(p => p.service_type_display))].filter(Boolean).map(st => (
                        <option key={st} value={st}>{st}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase font-semibold text-slate-500 block mb-1">Refresh Status</label>
                    <select
                      value={filterRefreshStatus}
                      onChange={(e) => setFilterRefreshStatus(e.target.value)}
                      className="w-full text-xs bg-slate-950 border border-slate-800 rounded p-1.5 text-slate-300 focus:outline-none focus:border-emerald-500"
                    >
                      <option value="all">All Refresh Statuses</option>
                      <option value="fresh">Fresh (Green)</option>
                      <option value="stale">Stale (Yellow)</option>
                      <option value="refresh_failed">Refresh Failed (Red)</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase font-semibold text-slate-500 block mb-1">Last Verified</label>
                    <select
                      value={filterLastVerified}
                      onChange={(e) => setFilterLastVerified(e.target.value)}
                      className="w-full text-xs bg-slate-950 border border-slate-800 rounded p-1.5 text-slate-300 focus:outline-none focus:border-emerald-500"
                    >
                      <option value="all">Any Verification Time</option>
                      <option value="never">Never Verified</option>
                      <option value="today">Verified</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase font-semibold text-slate-500 block mb-1">Action Required</label>
                    <select
                      value={filterActionRequired}
                      onChange={(e) => setFilterActionRequired(e.target.value)}
                      className="w-full text-xs bg-slate-950 border border-slate-800 rounded p-1.5 text-slate-300 focus:outline-none focus:border-emerald-500"
                    >
                      <option value="all">All Actions</option>
                      {[...new Set(patients.map(p => p.next_best_action))].filter(Boolean).map(ac => (
                        <option key={ac} value={ac}>{ac}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase font-semibold text-slate-500 block mb-1">Claim Risk</label>
                    <select
                      value={filterClaimRisk}
                      onChange={(e) => setFilterClaimRisk(e.target.value)}
                      className="w-full text-xs bg-slate-950 border border-slate-800 rounded p-1.5 text-slate-300 focus:outline-none focus:border-emerald-500"
                    >
                      <option value="all">All Risks</option>
                      <option value="Low">Low Risk</option>
                      <option value="Medium">Medium Risk</option>
                      <option value="High">High Risk</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase font-semibold text-slate-500 block mb-1">Prior Auth</label>
                    <select
                      value={filterPriorAuth}
                      onChange={(e) => setFilterPriorAuth(e.target.value)}
                      className="w-full text-xs bg-slate-950 border border-slate-800 rounded p-1.5 text-slate-300 focus:outline-none focus:border-emerald-500"
                    >
                      <option value="all">All Auth Statuses</option>
                      <option value="yes">PA Required</option>
                      <option value="no">PA Not Required</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase font-semibold text-slate-500 block mb-1">Appt Date</label>
                    <select
                      value={filterApptDate}
                      onChange={(e) => setFilterApptDate(e.target.value)}
                      className="w-full text-xs bg-slate-950 border border-slate-800 rounded p-1.5 text-slate-300 focus:outline-none focus:border-emerald-500"
                    >
                      <option value="all">All Dates</option>
                      <option value="2026-07-20">Jul 20, 2026</option>
                      <option value="2026-07-21">Jul 21, 2026</option>
                      <option value="2026-07-22">Jul 22, 2026</option>
                    </select>
                  </div>
                </div>

                {/* 10-Second UX Rule Quick Tabs */}
                <div className="pt-2 flex flex-wrap gap-2 border-t border-slate-800/40">
                  <span className="text-xs text-slate-400 flex items-center mr-2">Quick Answers:</span>
                  <button
                    onClick={() => {
                      setQuickFilter("all");
                      setFilterVisitReadiness("all");
                      setFilterLastVerified("all");
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition ${quickFilter === "all" ? "bg-slate-800 text-white" : "bg-slate-950 border border-slate-800 text-slate-400 hover:text-white"}`}
                  >
                    Show All
                  </button>
                  <button
                    onClick={() => {
                      setQuickFilter("ready");
                      setFilterVisitReadiness("ready");
                      setFilterLastVerified("all");
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition ${quickFilter === "ready" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" : "bg-slate-950 border border-slate-800 text-slate-400 hover:text-white"}`}
                  >
                    Who is Ready?
                  </button>
                  <button
                    onClick={() => {
                      setQuickFilter("attention");
                      setFilterVisitReadiness("attention_required");
                      setFilterLastVerified("all");
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition ${quickFilter === "attention" ? "bg-amber-500/20 text-amber-400 border-amber-500/30" : "bg-slate-950 border border-slate-800 text-slate-400 hover:text-white"}`}
                  >
                    Who Needs Attention?
                  </button>
                  <button
                    onClick={() => {
                      setQuickFilter("blocked");
                      setFilterVisitReadiness("blocked");
                      setFilterLastVerified("all");
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition ${quickFilter === "blocked" ? "bg-rose-500/20 text-rose-400 border-rose-500/30" : "bg-slate-950 border border-slate-800 text-slate-400 hover:text-white"}`}
                  >
                    Who is Blocked?
                  </button>
                  <button
                    onClick={() => {
                      setQuickFilter("needs_refresh");
                      setFilterLastVerified("never");
                      setFilterVisitReadiness("all");
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition ${quickFilter === "needs_refresh" ? "bg-indigo-500/20 text-indigo-400 border-indigo-500/30" : "bg-slate-950 border border-slate-800 text-slate-400 hover:text-white"}`}
                  >
                    Who Needs Eligibility Refresh?
                  </button>
                  <button
                    onClick={() => {
                      setQuickFilter("has_projected");
                      setFilterVisitReadiness("all");
                      setFilterLastVerified("all");
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition ${quickFilter === "has_projected" ? "bg-sky-500/20 text-sky-400 border-sky-500/30" : "bg-slate-950 border border-slate-800 text-slate-400 hover:text-white"}`}
                  >
                    Who has Projected Appointments?
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <Table className="border-collapse">
                  <TableHeader className="border-slate-800 bg-slate-900/40">
                    <TableRow className="border-slate-800 hover:bg-transparent">
                      <TableHead className="text-slate-400 font-semibold">Patient Name</TableHead>
                      <TableHead className="text-slate-400 font-semibold">Patient ID</TableHead>
                      <TableHead className="text-slate-400 font-semibold">Member ID</TableHead>
                      <TableHead className="text-slate-400 font-semibold">DOB</TableHead>
                      <TableHead className="text-slate-400 font-semibold">Payer</TableHead>
                      <TableHead className="text-slate-400 font-semibold">Service Type</TableHead>
                      <TableHead className="text-slate-400 font-semibold">Appt Time</TableHead>
                      <TableHead className="text-slate-400 font-semibold">Coverage Status</TableHead>
                      <TableHead className="text-slate-400 font-semibold">Visit Readiness</TableHead>
                      <TableHead className="text-slate-400 font-semibold">Last Verified</TableHead>
                      <TableHead className="text-slate-400 font-semibold">EMR Last Modified</TableHead>
                      <TableHead className="text-slate-400 font-semibold">Remaining Max</TableHead>
                      <TableHead className="text-slate-400 font-semibold">Remaining Ded</TableHead>
                      <TableHead className="text-slate-400 font-semibold">Claim Risk</TableHead>
                      <TableHead className="text-slate-400 font-semibold">Next Best Action</TableHead>
                      <TableHead className="text-slate-400 font-semibold">Projected Appts</TableHead>
                      <TableHead className="text-slate-400 font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedPatients.map((pat) => (
                      <TableRow key={pat.patient_id} className="border-slate-800/60 hover:bg-slate-800/20 text-slate-300">
                        <TableCell className="font-semibold text-white">
                          {pat.first_name} {pat.last_name}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-slate-400">{pat.patient_id}</TableCell>
                        <TableCell>{pat.member_id}</TableCell>
                        <TableCell>{pat.date_of_birth}</TableCell>
                        <TableCell>{pat.payer_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs border-slate-700 bg-slate-900/60 text-slate-300">
                            {pat.service_type_display}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-emerald-400 font-medium">{formatDateTime(pat.appointment_time)}</TableCell>
                        <TableCell>{getStatusBadge(pat.eligibility_status)}</TableCell>
                        <TableCell>{getReadinessBadge(pat.visit_readiness)}</TableCell>
                        <TableCell className="text-xs text-slate-400">{formatDateTime(pat.last_verified)}</TableCell>
                        <TableCell className="text-xs text-slate-400">{formatDateTime(pat.emr_last_modified || pat.updated_at)}</TableCell>
                        <TableCell className="font-semibold text-slate-300">
                          ${(pat.remaining_maximum ?? 1500).toLocaleString('en-US', {minimumFractionDigits: 2})}
                        </TableCell>
                        <TableCell className="text-slate-300">
                          ${(pat.remaining_deductible ?? 50).toLocaleString('en-US', {minimumFractionDigits: 2})}
                        </TableCell>
                        <TableCell>{getRiskBadge(pat.claim_risk_score, pat.claim_risk_category, pat.claim_risk_reasons)}</TableCell>
                        <TableCell className="text-xs font-medium text-indigo-300">{pat.next_best_action}</TableCell>
                        <TableCell>
                          {pat.projected_appointments && pat.projected_appointments.length > 0 ? (
                            <div className="group relative cursor-pointer inline-block">
                              <Badge className="bg-indigo-500/10 text-indigo-400 border-indigo-500/20 text-xs hover:bg-indigo-500/20">
                                {pat.projected_appointments.length} Appt(s)
                              </Badge>
                              <div className="absolute z-30 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-slate-950 border border-slate-800 rounded-xl p-3 shadow-2xl opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 text-left">
                                <p className="text-[10px] uppercase font-bold text-slate-500 mb-2">Next Predicted Care:</p>
                                <div className="space-y-2">
                                  {pat.projected_appointments.slice(0, 3).map((appt, idx) => (
                                    <div key={idx} className="border-b border-slate-800/60 pb-1.5 last:border-0 last:pb-0">
                                      <div className="flex justify-between items-center text-xs text-white font-medium">
                                        <span>{appt.visit_reason}</span>
                                        <span className="text-[10px] text-emerald-400 uppercase font-bold">{appt.priority}</span>
                                      </div>
                                      <div className="text-[10px] text-slate-400 flex justify-between mt-0.5">
                                        <span>Code: {appt.service_type_code}</span>
                                        <span>{appt.recommended_date}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-500">None</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button 
                              size="sm" 
                              onClick={() => handleOpenPatient(pat)}
                              className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                            >
                              Open
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleRefreshPatient(pat)}
                              disabled={refreshingMap[pat.patient_id || pat.external_patient_id]}
                              className="text-slate-400 hover:text-white hover:bg-slate-800"
                              title="Refresh Open Dental & Stedi Coverage Intelligence"
                            >
                              <RefreshCw className={`w-3.5 h-3.5 ${refreshingMap[pat.patient_id || pat.external_patient_id] ? "animate-spin text-emerald-400" : ""}`} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* COVERAGE INTELLIGENCE WORKSPACE */}
        {activePatient && workspaceData && (
          <div className="space-y-6">
            
            {/* Header info bar */}
            <div className="bg-[#0d1321] border border-slate-800 p-4 rounded-xl flex flex-wrap justify-between items-center gap-4 shadow-md sticky top-16 z-20 backdrop-blur-md">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-bold text-white">{activePatient.first_name} {activePatient.last_name}</h2>
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">Active Coverage</Badge>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Member ID: <span className="text-slate-300 font-semibold">{activePatient.member_id}</span> · 
                    DOB: <span className="text-slate-300 font-semibold">{activePatient.date_of_birth}</span> · 
                    Payer: <span className="text-slate-300 font-semibold">{activePatient.payer_name}</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">Readiness Status:</span>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg border text-xs font-semibold ${readiness.color}`}>
                  <readiness.icon className="w-3.5 h-3.5" />
                  {readiness.status}
                </span>
              </div>
            </div>

            {/* Split layout grid */}
            <div className="flex gap-6 relative items-start">
              
              {/* COLLAPSIBLE LEFT NAVIGATION SIDEBAR */}
              <div 
                className={`sticky top-36 shrink-0 z-10 transition-all duration-300 bg-[#0d1321]/80 backdrop-blur-md border border-slate-800 rounded-xl overflow-hidden shadow-lg ${
                  sidebarCollapsed ? "w-16" : "w-80"
                }`}
              >
                {/* Collapse/Expand Toggle Header */}
                <div className="flex items-center justify-between border-b border-slate-800 p-3 bg-slate-900/40">
                  {!sidebarCollapsed && (
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                      <List className="w-4 h-4 text-emerald-400" />
                      Workspace Nav
                    </span>
                  )}
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    className="h-7 w-7 text-slate-400 hover:text-white mx-auto"
                  >
                    {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                  </Button>
                </div>

                {/* Sidebar Navigation Items */}
                <div className="p-2.5 max-h-[70vh] overflow-y-auto space-y-1">
                  {NAV_ITEMS.map((item) => {
                    const IconComp = item.icon;
                    const isActive = activeNavSection === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        title={item.name}
                        className={`w-full text-left rounded-lg text-xs font-medium transition-all flex items-center gap-3 p-2.5 border ${
                          isActive 
                            ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/25 font-bold" 
                            : "text-slate-400 hover:text-white hover:bg-slate-800/40 border-transparent"
                        }`}
                      >
                        <IconComp className={`w-4 h-4 shrink-0 ${isActive ? "text-emerald-400" : "text-slate-400"}`} />
                        {!sidebarCollapsed && <span className="truncate">{item.name}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Main Content Area */}
              <div className="flex-1 space-y-8 min-w-0">

                {/* TOP WORKSPACE KPI CARDS */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-11 gap-3">
                  {/* Card 1: Coverage Status */}
                  <div className="bg-slate-900/50 border border-slate-800 p-3 rounded-xl flex flex-col justify-between shadow-sm">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Coverage Status</span>
                    <span className="text-sm font-bold text-emerald-400 mt-1">Active</span>
                  </div>
                  
                  {/* Card 2: Visit Readiness */}
                  <div className="bg-slate-900/50 border border-slate-800 p-3 rounded-xl flex flex-col justify-between shadow-sm">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Visit Readiness</span>
                    <span className="text-sm font-bold text-amber-400 mt-1">Attention Req</span>
                  </div>

                  {/* Card 3: Est Responsibility */}
                  <div className="bg-slate-900/50 border border-slate-800 p-3 rounded-xl flex flex-col justify-between shadow-sm">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Est Responsibility</span>
                    <span className="text-sm font-bold text-white mt-1">$650.00</span>
                  </div>

                  {/* Card 4: Remaining Benefit */}
                  <div className="bg-slate-900/50 border border-slate-800 p-3 rounded-xl flex flex-col justify-between shadow-sm">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Remaining Benefit</span>
                    <span className="text-xs font-bold text-emerald-400 mt-1">$1,450 / $2,000</span>
                  </div>

                  {/* Card 5: Remaining Deductible */}
                  <div className="bg-slate-900/50 border border-slate-800 p-3 rounded-xl flex flex-col justify-between shadow-sm">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Remaining Deductible</span>
                    <span className="text-sm font-bold text-emerald-400 mt-1">$0.00</span>
                  </div>

                  {/* Card 6: Prior Auth Risk */}
                  <div className="bg-slate-900/50 border border-slate-800 p-3 rounded-xl flex flex-col justify-between shadow-sm">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Prior Auth Risk</span>
                    <span className="text-xs font-bold text-rose-400 mt-1">High (D3330)</span>
                  </div>

                  {/* Card 7: Predicted Next Appt */}
                  <div className="bg-slate-900/50 border border-slate-800 p-3 rounded-xl flex flex-col justify-between shadow-sm col-span-1">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Predicted Appt</span>
                    <span className="text-[10px] font-bold text-indigo-300 mt-1 truncate">Crown Prep (Today)</span>
                  </div>

                  {/* Card 8: Next 3 Appointments */}
                  <div className="bg-slate-900/50 border border-slate-800 p-3 rounded-xl flex flex-col justify-between shadow-sm col-span-1 lg:col-span-2">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Next 3 Appts</span>
                    <span className="text-[9px] text-slate-300 mt-1 font-mono leading-tight">
                      1. Prep (Today) <br />
                      2. Delivery (+14d) <br />
                      3. Adjust (+21d)
                    </span>
                  </div>

                  {/* Card 9: EMR Sync Rec */}
                  <div className="bg-slate-900/50 border border-slate-800 p-3 rounded-xl flex flex-col justify-between shadow-sm">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">EMR Sync Rec</span>
                    <span className="text-sm font-bold text-indigo-400 mt-1">
                      {Object.values(emrSyncs).filter(Boolean).length} Task(s)
                    </span>
                  </div>

                  {/* Card 10: Reset Countdown */}
                  <div className="bg-slate-900/50 border border-slate-800 p-3 rounded-xl flex flex-col justify-between shadow-sm">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Reset Days</span>
                    <span className="text-sm font-bold text-sky-400 mt-1">177 Days</span>
                  </div>
                </div>
                
                {/* Next Best Action Banner */}
                <div className="bg-indigo-950/40 border border-indigo-800/40 p-3.5 rounded-xl flex items-center justify-between gap-4 shadow-sm">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-indigo-400" />
                    <span className="text-xs text-slate-300">
                      <strong className="text-white font-semibold">Next Best Action:</strong> {activePatient.next_best_action || "Estimate Patient Cost"}
                    </span>
                  </div>
                  <Button size="xs" className="bg-indigo-600 hover:bg-indigo-500 text-white text-[10px]" onClick={triggerCostEstimate}>
                    Execute Action
                  </Button>
                </div>

                {/* Section 1: Member Summary */}
                <div id="section-1" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 pb-4">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-base font-bold text-white">Section 1 - Member Summary</CardTitle>
                        <div className="flex items-center gap-2">
                          <Button size="xs" variant="outline" className="border-slate-800 text-xs py-1" onClick={triggerDownloadPDF}>
                            <FileDown className="w-3.5 h-3.5 mr-1" /> Download Eligibility PDF
                          </Button>
                          <Button size="xs" variant="outline" className="border-slate-800 text-xs py-1" onClick={() => checkEligibility(activePatient)}>
                            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh Eligibility
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-5">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-slate-300">
                        
                        <div className="space-y-3">
                          <div className="flex justify-between border-b border-slate-800/40 pb-1.5">
                            <span className="text-slate-400">Patient Name:</span>
                            <span className="text-white font-medium">{activePatient.first_name} {activePatient.last_name}</span>
                          </div>
                          <div className="flex justify-between border-b border-slate-800/40 pb-1.5">
                            <span className="text-slate-400">Patient ID:</span>
                            <span className="font-mono text-xs text-white">{activePatient.patient_id}</span>
                          </div>
                          <div className="flex justify-between border-b border-slate-800/40 pb-1.5 items-center">
                            <span className="text-slate-400">Member ID:</span>
                            <div className="flex items-center gap-1.5">
                              <span className="text-white font-semibold">{activePatient.member_id}</span>
                              <button onClick={() => handleCopyText(activePatient.member_id, "Member ID")} className="text-slate-500 hover:text-white" title="Copy Member ID">
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <div className="flex justify-between border-b border-slate-800/40 pb-1.5">
                            <span className="text-slate-400">DOB:</span>
                            <span className="text-white">{activePatient.date_of_birth}</span>
                          </div>
                          <div className="flex justify-between pb-1.5">
                            <span className="text-slate-400">Relationship:</span>
                            <span className="text-white">{workspaceData.coverage_summary?.member?.subscriber?.relationship || "Self"}</span>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex justify-between border-b border-slate-800/40 pb-1.5">
                            <span className="text-slate-400">Payer Name:</span>
                            <span className="text-white font-medium">{workspaceData.coverage_summary?.payer?.name || activePatient.payer_name}</span>
                          </div>
                          <div className="flex justify-between border-b border-slate-800/40 pb-1.5">
                            <span className="text-slate-400">Payer ID:</span>
                            <span className="text-white">{workspaceData.coverage_summary?.payer?.payorIdentification || "--"}</span>
                          </div>
                          <div className="flex justify-between border-b border-slate-800/40 pb-1.5 items-center">
                            <span className="text-slate-400">Group Number:</span>
                            <div className="flex items-center gap-1.5">
                              <span className="text-white">{workspaceData.coverage_summary?.plan?.groupNumber || "--"}</span>
                              {workspaceData.coverage_summary?.plan?.groupNumber && (
                                <button onClick={() => handleCopyText(workspaceData.coverage_summary.plan.groupNumber, "Group Number")} className="text-slate-500 hover:text-white" title="Copy Group Number">
                                  <Copy className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="flex justify-between border-b border-slate-800/40 pb-1.5">
                            <span className="text-slate-400">Plan Name:</span>
                            <span className="text-emerald-400 font-semibold">{workspaceData.coverage_summary?.plan?.planName || "--"}</span>
                          </div>
                          <div className="flex justify-between pb-1.5">
                            <span className="text-slate-400">Plan Type:</span>
                            <span className="text-white">{workspaceData.coverage_summary?.plan?.planType || "--"}</span>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex justify-between border-b border-slate-800/40 pb-1.5">
                            <span className="text-slate-400">Coverage Status:</span>
                            <span className="text-emerald-400 font-semibold">Active</span>
                          </div>
                          <div className="flex justify-between border-b border-slate-800/40 pb-1.5">
                            <span className="text-slate-400">Effective Date:</span>
                            <span className="text-white">{workspaceData.coverage_summary?.plan?.effectiveDate || "--"}</span>
                          </div>
                          <div className="flex justify-between border-b border-slate-800/40 pb-1.5">
                            <span className="text-slate-400">End Date:</span>
                            <span className="text-white">{workspaceData.coverage_summary?.plan?.endDate || "N/A"}</span>
                          </div>
                          <div className="flex justify-between border-b border-slate-800/40 pb-1.5">
                            <span className="text-slate-400">Verification Time:</span>
                            <span className="text-white font-mono text-xs">{formatPracticeTime(workspaceData.verification_timestamp, workspaceData.practice_timezone)}</span>
                          </div>
                          <div className="flex justify-between pb-1.5 truncate">
                            <span className="text-slate-400 shrink-0">Transaction ID:</span>
                            <span className="text-white font-mono text-[10px] truncate ml-2">TXN-ANT-982173B</span>
                          </div>
                        </div>

                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 2: Coverage Overview */}
                <div id="section-2" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 pb-3">
                      <CardTitle className="text-base font-bold text-white">Section 2 - Coverage Overview</CardTitle>
                    </CardHeader>
                    <CardContent className="p-5">
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="bg-slate-900/60 border border-slate-800 p-4 rounded-xl text-center">
                          <div className="text-[10px] text-slate-400 font-semibold mb-1.5">Coverage Active</div>
                          <div className="flex items-center justify-center gap-1.5 text-emerald-400 font-bold">
                            <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse" />
                            Yes
                          </div>
                        </div>
                        <div className="bg-slate-900/60 border border-slate-800 p-4 rounded-xl text-center">
                          <div className="text-[10px] text-slate-400 font-semibold mb-1.5">Dental Coverage</div>
                          <div className="text-emerald-400 font-bold">Active</div>
                        </div>
                        <div className="bg-slate-900/60 border border-slate-800 p-4 rounded-xl text-center">
                          <div className="text-[10px] text-slate-400 font-semibold mb-1.5">Medical Coverage</div>
                          <div className="text-slate-500 font-bold">Not Found</div>
                        </div>
                        <div className="bg-slate-900/60 border border-slate-800 p-4 rounded-xl text-center">
                          <div className="text-[10px] text-slate-400 font-semibold mb-1.5">Individual Coverage</div>
                          <div className="text-emerald-400 font-bold">Yes</div>
                        </div>
                        <div className="bg-slate-900/60 border border-slate-800 p-4 rounded-xl text-center">
                          <div className="text-[10px] text-slate-400 font-semibold mb-1.5">Family Coverage</div>
                          <div className="text-slate-500 font-bold">No</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 3: Financial Summary */}
                <div id="section-3" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 pb-3">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-base font-bold text-white">Section 3 - Financial Summary</CardTitle>
                        <div className="flex gap-2">
                          <Button size="xs" variant="outline" className="border-slate-800 text-xs py-1" onClick={triggerCostEstimate}>
                            Estimate Patient Cost
                          </Button>
                          <Button size="xs" variant="outline" className="border-slate-800 text-xs py-1" onClick={triggerPrint}>
                            Print Financial Summary
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader className="border-slate-800 bg-slate-900/30">
                          <TableRow className="border-slate-800 hover:bg-transparent">
                            <TableHead className="text-slate-400 font-semibold">Benefit</TableHead>
                            <TableHead className="text-slate-400 font-semibold text-right">Total</TableHead>
                            <TableHead className="text-slate-400 font-semibold text-right">Remaining</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow className="border-slate-800/60 hover:bg-transparent text-slate-300">
                            <TableCell className="font-semibold text-white">Annual Maximum</TableCell>
                            <TableCell className="text-right font-mono">$2,000</TableCell>
                            <TableCell className="text-right font-mono text-emerald-400">$1,450</TableCell>
                          </TableRow>
                          <TableRow className="border-slate-800/60 hover:bg-transparent text-slate-300">
                            <TableCell className="font-semibold text-white">Annual Maximum Remaining</TableCell>
                            <TableCell className="text-right font-mono">$2,000</TableCell>
                            <TableCell className="text-right font-mono text-emerald-400">$1,450</TableCell>
                          </TableRow>
                          <TableRow className="border-slate-800/60 hover:bg-transparent text-slate-300">
                            <TableCell className="font-semibold text-white">Individual Deductible</TableCell>
                            <TableCell className="text-right font-mono">$50</TableCell>
                            <TableCell className="text-right font-mono text-emerald-400">$0</TableCell>
                          </TableRow>
                          <TableRow className="border-slate-800/60 hover:bg-transparent text-slate-300">
                            <TableCell className="font-semibold text-white">Remaining Deductible</TableCell>
                            <TableCell className="text-right font-mono">$50</TableCell>
                            <TableCell className="text-right font-mono text-emerald-400">$0</TableCell>
                          </TableRow>
                          <TableRow className="border-slate-800/60 hover:bg-transparent text-slate-300">
                            <TableCell className="font-semibold text-white">Family Deductible</TableCell>
                            <TableCell className="text-right font-mono">$150</TableCell>
                            <TableCell className="text-right font-mono text-slate-300">$150</TableCell>
                          </TableRow>
                          <TableRow className="border-slate-800/60 hover:bg-transparent text-slate-300">
                            <TableCell className="font-semibold text-white">Remaining Family Deductible</TableCell>
                            <TableCell className="text-right font-mono">$150</TableCell>
                            <TableCell className="text-right font-mono text-slate-300">$150</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 4: Dental Benefit Matrix */}
                <div id="section-4" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 pb-3">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-base font-bold text-white">Section 4 - Dental Benefit Matrix</CardTitle>
                        <span className="text-xs text-emerald-400 cursor-pointer hover:underline" onClick={() => toast.info(" CDTs loaded in Section 15 Procedure Details.")}>
                          View CDT Codes
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader className="border-slate-800 bg-slate-900/30">
                          <TableRow className="border-slate-800 hover:bg-transparent">
                            <TableHead className="text-slate-400 font-semibold">Category</TableHead>
                            <TableHead className="text-slate-400 font-semibold">In Network</TableHead>
                            <TableHead className="text-slate-400 font-semibold">Out of Network</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(workspaceData.coverage_summary?.benefits?.matrix || {}).map(([key, val]) => {
                            const formattedKey = key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                            return (
                              <TableRow key={key} className="border-slate-800/60 hover:bg-transparent text-slate-300">
                                <TableCell className="font-medium text-white">{formattedKey}</TableCell>
                                <TableCell className="font-mono text-emerald-400">{val.in_network}</TableCell>
                                <TableCell className="font-mono text-slate-400">{val.out_of_network}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 5: Network Comparison */}
                <div id="section-5" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 pb-3">
                      <CardTitle className="text-base font-bold text-white">Section 5 - Network Comparison</CardTitle>
                    </CardHeader>
                    <CardContent className="p-5">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        
                        <div className="border border-emerald-500/30 rounded-xl p-4 bg-emerald-500/5 relative">
                          <Badge className="absolute top-3 right-3 bg-emerald-500 text-slate-900 font-bold">Lowest Cost</Badge>
                          <h4 className="text-white font-bold mb-3 flex items-center gap-1.5">
                            <Award className="w-4 h-4 text-emerald-400" />
                            In Network (Preferred)
                          </h4>
                          <div className="space-y-2.5 text-xs">
                            <div className="flex justify-between border-b border-slate-800/40 pb-1.5 text-slate-300">
                              <span>Preventive Care:</span>
                              <span className="text-emerald-400 font-semibold">0% Responsibility</span>
                            </div>
                            <div className="flex justify-between border-b border-slate-800/40 pb-1.5 text-slate-300">
                              <span>Diagnostic Services:</span>
                              <span className="text-emerald-400 font-semibold">0% Responsibility</span>
                            </div>
                            <div className="flex justify-between border-b border-slate-800/40 pb-1.5 text-slate-300">
                              <span>Basic Fillings:</span>
                              <span className="text-white font-semibold">20% Responsibility</span>
                            </div>
                            <div className="flex justify-between text-slate-300">
                              <span>Major/Crowns:</span>
                              <span className="text-white font-semibold">50% Responsibility</span>
                            </div>
                          </div>
                        </div>

                        <div className="border border-slate-800 rounded-xl p-4 bg-slate-900/10">
                          <h4 className="text-slate-300 font-bold mb-3 flex items-center gap-1.5">
                            <XCircle className="w-4 h-4 text-slate-500" />
                            Out of Network
                          </h4>
                          <div className="space-y-2.5 text-xs">
                            <div className="flex justify-between border-b border-slate-800/40 pb-1.5 text-slate-400">
                              <span>Preventive Care:</span>
                              <span>20% Responsibility</span>
                            </div>
                            <div className="flex justify-between border-b border-slate-800/40 pb-1.5 text-slate-400">
                              <span>Diagnostic Services:</span>
                              <span>20% Responsibility</span>
                            </div>
                            <div className="flex justify-between border-b border-slate-800/40 pb-1.5 text-slate-400">
                              <span>Basic Fillings:</span>
                              <span>40% Responsibility</span>
                            </div>
                            <div className="flex justify-between text-slate-400">
                              <span>Major/Crowns:</span>
                              <span>60% Responsibility</span>
                            </div>
                          </div>
                        </div>

                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 6: Coverage Limitations */}
                <div id="section-6" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 p-4">
                      <CardTitle className="text-sm font-bold text-white">Section 6 - Coverage Limitations</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 space-y-3 text-xs text-slate-300">
                      <div className="flex justify-between border-b border-slate-800/40 pb-2">
                        <span className="text-slate-400">Waiting Period:</span>
                        <span className="text-white font-medium">{workspaceData.coverage_summary?.benefits?.limitations?.waiting_period || "None"}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-800/40 pb-2">
                        <span className="text-slate-400">Age Limits:</span>
                        <span className="text-white font-medium">{workspaceData.coverage_summary?.benefits?.limitations?.age_limits || "N/A"}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-800/40 pb-2">
                        <span className="text-slate-400">Lifetime Maximum:</span>
                        <span className="text-white font-medium">{workspaceData.coverage_summary?.benefits?.limitations?.lifetime_maximum || "N/A"}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-800/40 pb-2 items-center">
                        <span className="text-slate-400">Remaining Maximum:</span>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">{workspaceData.coverage_summary?.benefits?.limitations?.remaining_maximum || "N/A"}</span>
                          <button 
                            onClick={() => handleShowEvidence("Remaining Maximum", "benefits.limitations.remaining_maximum")}
                            className="text-[10px] text-indigo-400 hover:text-indigo-300 hover:underline"
                          >
                            View Source
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between border-b border-slate-800/40 pb-2">
                        <span className="text-slate-400">Frequency Limits:</span>
                        <span className="text-white font-medium">{workspaceData.coverage_summary?.benefits?.limitations?.frequency_limits || "N/A"}</span>
                      </div>
                      <div className="flex justify-between pb-1">
                        <span className="text-slate-400">Visit Limits:</span>
                        <span className="text-white font-medium">{workspaceData.coverage_summary?.benefits?.limitations?.visit_limits || "N/A"}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 7: Deductibles */}
                <div id="section-7" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 p-4">
                      <CardTitle className="text-sm font-bold text-white">Section 7 - Deductibles</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader className="bg-slate-900/30">
                          <TableRow className="border-slate-800">
                            <TableHead className="text-slate-400">Type</TableHead>
                            <TableHead className="text-slate-400 text-right">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow className="border-slate-800/40 hover:bg-transparent text-slate-300">
                            <TableCell>Individual</TableCell>
                            <TableCell className="text-right font-mono text-white flex items-center justify-end gap-2">
                              <span>${workspaceData.coverage_summary?.benefits?.deductibles?.individual || "0"}</span>
                              <button 
                                onClick={() => handleShowEvidence("Individual Deductible", "benefits.deductibles.individual")}
                                className="text-[10px] text-indigo-400 hover:text-indigo-300 hover:underline font-sans"
                              >
                                View Source
                              </button>
                            </TableCell>
                          </TableRow>
                          <TableRow className="border-slate-800/40 hover:bg-transparent text-slate-300">
                            <TableCell>Family</TableCell>
                            <TableCell className="text-right font-mono text-white">${workspaceData.coverage_summary?.benefits?.deductibles?.family || "0"}</TableCell>
                          </TableRow>
                          <TableRow className="border-slate-800/40 hover:bg-transparent text-slate-300">
                            <TableCell>Dental</TableCell>
                            <TableCell className="text-right font-mono text-white">${workspaceData.coverage_summary?.benefits?.deductibles?.dental || "0"}</TableCell>
                          </TableRow>
                          <TableRow className="border-slate-800/40 hover:bg-transparent text-slate-300">
                            <TableCell>Orthodontic</TableCell>
                            <TableCell className="text-right font-mono text-white">${workspaceData.coverage_summary?.benefits?.deductibles?.orthodontic || "0"}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 8: Coverage Messages */}
                <div id="section-8" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 p-4">
                      <CardTitle className="text-sm font-bold text-white">Section 8 - Coverage Messages</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 space-y-2.5 text-xs text-slate-400">
                      {workspaceData.coverage_summary?.benefits?.messages?.map((msg, idx) => (
                        <div key={idx} className="flex items-start gap-2 bg-slate-900/40 border border-slate-800/60 p-2.5 rounded">
                          <Info className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                          <span>{msg}</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>

                {/* Section 9: Service Types */}
                <div id="section-9" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 p-4">
                      <CardTitle className="text-sm font-bold text-white">Section 9 - Service Types</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="flex flex-wrap gap-2">
                        <button 
                          onClick={() => setSelectedServiceFilter("all")}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                            selectedServiceFilter === "all" 
                              ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" 
                              : "bg-slate-900 border-slate-850 text-slate-400 hover:text-white"
                          }`}
                        >
                          All Service Types
                        </button>
                        {workspaceData.coverage_summary?.benefits?.service_types?.map((st) => (
                          <button 
                            key={st.code}
                            onClick={() => setSelectedServiceFilter(st.code)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border flex items-center gap-2 ${
                              selectedServiceFilter === st.code 
                                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" 
                                : "bg-slate-900 border-slate-850 text-slate-400 hover:text-white"
                            }`}
                          >
                            <span>{st.label}</span>
                            <span className="font-mono text-[9px] px-1 bg-slate-800 rounded text-slate-500">#{st.code}</span>
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 10: Coverage Intelligence Insights */}
                <div id="section-10" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 p-4">
                      <CardTitle className="text-sm font-bold text-white flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-emerald-400" />
                        Section 10 - Coverage Intelligence Insights
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="bg-slate-900/40 border border-slate-800 p-3 rounded-lg flex items-center gap-2.5 text-xs text-slate-200">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                          <span>Coverage verified today (Active Dental PPO)</span>
                        </div>
                        <div className="bg-slate-900/40 border border-slate-800 p-3 rounded-lg flex items-center gap-2.5 text-xs text-slate-200">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                          <span>Remaining annual maximum available ($1,450)</span>
                        </div>
                        <div className="bg-slate-900/40 border border-slate-800 p-3 rounded-lg flex items-center gap-2.5 text-xs text-slate-200">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                          <span>Deductible fully met ($0 remaining)</span>
                        </div>
                        <div className="bg-slate-900/40 border border-slate-800 p-3 rounded-lg flex items-center gap-2.5 text-xs text-slate-200">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                          <span>Preventive and diagnostic covered at 100% in-network</span>
                        </div>
                        <div className="bg-slate-900/40 border border-slate-800 p-3 rounded-lg flex items-center gap-2.5 text-xs text-slate-200">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                          <span>Crowns/major services covered at 50% in-network</span>
                        </div>
                        <div className="bg-slate-900/40 border border-slate-800 p-3 rounded-lg flex items-center gap-2.5 text-xs text-slate-200">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                          <span>No waiting period applies for active members</span>
                        </div>
                        <div className="bg-slate-900/40 border border-slate-800 p-3 rounded-lg flex items-center gap-2.5 text-xs text-slate-200">
                          <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
                          <span>Orthodontics limited by patient age under 19</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 11: Visit Readiness */}
                <div id="section-11" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 p-4">
                      <CardTitle className="text-sm font-bold text-white">Section 11 - Visit Readiness</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 space-y-4">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 bg-slate-900/60 border border-slate-800 rounded-lg gap-3">
                        <div>
                          <div className="text-xs text-slate-400 font-semibold">Can today's visit proceed?</div>
                          <div className={`text-lg font-bold mt-0.5 ${readiness.status === "Ready" ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {readiness.status === "Ready" ? "Ready" : "Attention Required"}
                          </div>
                        </div>
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold ${readiness.color}`}>
                          <readiness.icon className="w-4 h-4" />
                          Overall Status: {readiness.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                        <div className="bg-slate-900/20 border border-slate-800/60 p-3 rounded-lg flex items-center gap-3">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                          <div>
                            <div className="text-white font-semibold">Eligibility Verified</div>
                            <div className="text-[10px] text-slate-500">271 validation succeeded</div>
                          </div>
                        </div>
                        <div className="bg-slate-900/20 border border-slate-800/60 p-3 rounded-lg flex items-center gap-3">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                          <div>
                            <div className="text-white font-semibold">Coverage Active</div>
                            <div className="text-[10px] text-slate-500">Plan benefits currently active</div>
                          </div>
                        </div>
                        <div className="bg-slate-900/20 border border-slate-800/60 p-3 rounded-lg flex items-center gap-3">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                          <div>
                            <div className="text-white font-semibold">Provider In Network</div>
                            <div className="text-[10px] text-slate-500">Contracted with Anthem PPO</div>
                          </div>
                        </div>
                        <div className="bg-slate-900/20 border border-slate-800/60 p-3 rounded-lg flex items-center gap-3">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                          <div>
                            <div className="text-white font-semibold">Benefits Available</div>
                            <div className="text-[10px] text-slate-500">Balance remains on annual cap</div>
                          </div>
                        </div>
                        <div className="bg-slate-900/20 border border-slate-800/60 p-3 rounded-lg flex items-center gap-3 md:col-span-2">
                          <button 
                            onClick={() => setAdminChecklist(prev => ({ ...prev, estimateGenerated: !prev.estimateGenerated }))}
                            className="shrink-0"
                          >
                            {adminChecklist.estimateGenerated ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                            ) : (
                              <div className="w-4 h-4 rounded-full border border-slate-700 hover:border-slate-500" />
                            )}
                          </button>
                          <div>
                            <div className="text-white font-semibold">Financial Estimate Ready</div>
                            <div className="text-[10px] text-slate-500">Co-insurance calculated and documented</div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 12: Recommendations */}
                <div id="section-12" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 p-4">
                      <CardTitle className="text-sm font-bold text-white">Section 12 - Recommendations</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 space-y-3">
                      <div className="bg-slate-900/40 border border-slate-800 p-3 rounded flex justify-between items-center hover:border-slate-700 cursor-pointer" onClick={triggerCostEstimate}>
                        <div>
                          <div className="text-xs font-semibold text-emerald-400">Estimate Patient Cost</div>
                          <p className="text-[10px] text-slate-500 mt-0.5">Calculate patient out-of-pocket costs before proceeding.</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-500" />
                      </div>
                      <div className="bg-slate-900/40 border border-slate-800 p-3 rounded flex justify-between items-center">
                        <div>
                          <div className="text-xs font-semibold text-white">Benefits Verified</div>
                          <p className="text-[10px] text-slate-500 mt-0.5">Confirm periodic exam limits are not exceeded.</p>
                        </div>
                      </div>
                      <div className="bg-slate-900/40 border border-slate-800 p-3 rounded flex justify-between items-center">
                        <div>
                          <div className="text-xs font-semibold text-white">Review Coverage Limitation</div>
                          <p className="text-[10px] text-slate-500 mt-0.5">Validate orthodontic treatment plan matches &lt; 19 age limit.</p>
                        </div>
                      </div>
                      <div className="bg-slate-900/40 border border-slate-800 p-3 rounded flex justify-between items-center">
                        <div>
                          <div className="text-xs font-semibold text-white">Review Waiting Period</div>
                          <p className="text-[10px] text-slate-500 mt-0.5">Continuous group coverage satisfies waiting requirements.</p>
                        </div>
                      </div>
                      <div className="bg-slate-900/40 border border-slate-800 p-3 rounded flex justify-between items-center">
                        <div>
                          <div className="text-xs font-semibold text-amber-400">Review Prior Authorization</div>
                          <p className="text-[10px] text-slate-500 mt-0.5">Submit pre-determination auth request for crown procedure.</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 13: Administrative Checklist */}
                <div id="section-13" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 p-4">
                      <CardTitle className="text-sm font-bold text-white">Section 13 - Administrative Checklist</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <label className="flex items-center gap-3 cursor-pointer text-xs text-slate-300 p-2 border border-slate-800 rounded bg-slate-900/20">
                          <input 
                            type="checkbox" 
                            checked={adminChecklist.eligibilityVerified}
                            onChange={(e) => setAdminChecklist(prev => ({ ...prev, eligibilityVerified: e.target.checked }))}
                            className="accent-emerald-500 rounded border-slate-700 bg-slate-800"
                          />
                          <span>Eligibility Verified</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer text-xs text-slate-300 p-2 border border-slate-800 rounded bg-slate-900/20">
                          <input 
                            type="checkbox" 
                            checked={adminChecklist.benefitsReviewed}
                            onChange={(e) => setAdminChecklist(prev => ({ ...prev, benefitsReviewed: e.target.checked }))}
                            className="accent-emerald-500 rounded border-slate-700 bg-slate-800"
                          />
                          <span>Benefits Reviewed</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer text-xs text-slate-300 p-2 border border-slate-800 rounded bg-slate-900/20">
                          <input 
                            type="checkbox" 
                            checked={adminChecklist.estimateGenerated}
                            onChange={(e) => setAdminChecklist(prev => ({ ...prev, estimateGenerated: e.target.checked }))}
                            className="accent-emerald-500 rounded border-slate-700 bg-slate-800"
                          />
                          <span>Estimate Generated</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer text-xs text-slate-300 p-2 border border-slate-800 rounded bg-slate-900/20">
                          <input 
                            type="checkbox" 
                            checked={adminChecklist.patientNotified}
                            onChange={(e) => setAdminChecklist(prev => ({ ...prev, patientNotified: e.target.checked }))}
                            className="accent-emerald-500 rounded border-slate-700 bg-slate-800"
                          />
                          <span>Patient Notified</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer text-xs text-slate-300 p-2 border border-slate-800 rounded bg-slate-900/20 sm:col-span-2">
                          <input 
                            type="checkbox" 
                            checked={adminChecklist.coverageSaved}
                            onChange={(e) => setAdminChecklist(prev => ({ ...prev, coverageSaved: e.target.checked }))}
                            className="accent-emerald-500 rounded border-slate-700 bg-slate-800"
                          />
                          <span>Coverage Saved</span>
                        </label>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 14: Timeline */}
                <div id="section-14" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 p-4">
                      <CardTitle className="text-sm font-bold text-white flex items-center gap-2">
                        <Clock className="w-4 h-4 text-indigo-400" />
                        Patient Coverage & Benefit History Timeline
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-5">
                      <div className="space-y-6 relative before:absolute before:inset-0 before:left-3 before:h-full before:w-0.5 before:bg-slate-800">
                        {workspaceData.timeline?.map((yrData, yrIdx) => (
                          <div key={yrIdx} className="relative pl-8 space-y-2">
                            {/* Circle Pin */}
                            <div className="absolute left-1.5 top-1 w-3.5 h-3.5 rounded-full bg-indigo-500 border-2 border-slate-950 z-10 shadow-sm" />
                            
                            <h4 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                              {yrData.year}
                              <Badge className="bg-indigo-500/10 text-indigo-400 border-indigo-500/20 text-[9px] font-semibold py-0">
                                {yrData.events?.length || 0} Event(s)
                              </Badge>
                            </h4>
                            <ul className="space-y-1.5">
                              {yrData.events?.map((evt, evtIdx) => (
                                <li key={evtIdx} className="text-xs text-slate-300 flex items-start gap-2">
                                  <span className="text-indigo-400 mt-0.5">•</span>
                                  <span>{evt}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 15: Procedure Details */}
                <div id="section-15" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 pb-3">
                      <CardTitle className="text-base font-bold text-white">Section 15 - Procedure Details</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader className="border-slate-800 bg-slate-900/30">
                          <TableRow className="border-slate-800 hover:bg-transparent">
                            <TableHead className="text-slate-400 font-semibold">CDT / CPT Code</TableHead>
                            <TableHead className="text-slate-400 font-semibold">Description</TableHead>
                            <TableHead className="text-slate-400 font-semibold">Coverage</TableHead>
                            <TableHead className="text-slate-400 font-semibold">Patient Responsibility</TableHead>
                            <TableHead className="text-slate-400 font-semibold">Authorization Required</TableHead>
                            <TableHead className="text-slate-400 font-semibold">Notes</TableHead>
                            <TableHead className="text-slate-400 font-semibold text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {workspaceData.coverage_summary?.benefits?.procedures
                            ?.filter(p => selectedServiceFilter === "all" || (selectedServiceFilter === "35" && p.code.startsWith("D")) || (selectedServiceFilter === "30" && !p.code.startsWith("D")))
                            ?.map((proc) => (
                            <TableRow key={proc.code} className="border-slate-800/60 hover:bg-transparent text-slate-300 text-xs">
                              <TableCell className="font-semibold text-white font-mono">{proc.code}</TableCell>
                              <TableCell>{proc.description}</TableCell>
                              <TableCell className="font-mono text-emerald-400">{proc.coverage}</TableCell>
                              <TableCell className="font-mono text-white">{proc.responsibility}</TableCell>
                              <TableCell>
                                {proc.auth_required ? (
                                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px]">Yes</Badge>
                                ) : (
                                  <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-[10px]">No</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-slate-400 italic">{proc.notes}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1.5">
                                  <Button size="xs" variant="ghost" className="text-[10px] h-7 text-slate-400 hover:text-white" onClick={triggerCostEstimate}>
                                    Estimate Cost
                                  </Button>
                                  <Button size="xs" variant="ghost" className="text-[10px] h-7 text-emerald-400 hover:text-emerald-300" onClick={() => toast.success("Added to Treatment Plan")}>
                                    Add to Treatment Plan
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 16 — Care Journey Prediction */}
                <div id="section-16" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 pb-3">
                      <CardTitle className="text-base font-bold text-white flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-indigo-400" />
                        Section 16 - Care Journey Prediction
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader className="border-slate-800 bg-slate-900/30">
                          <TableRow className="border-slate-800 hover:bg-transparent">
                            <TableHead className="text-slate-400 font-semibold w-16">Seq</TableHead>
                            <TableHead className="text-slate-400 font-semibold">Predicted Appointment</TableHead>
                            <TableHead className="text-slate-400 font-semibold">Recommended Date</TableHead>
                            <TableHead className="text-slate-400 font-semibold">Priority</TableHead>
                            <TableHead className="text-slate-400 font-semibold">Sync to EMR</TableHead>
                            <TableHead className="text-slate-400 font-semibold text-right">Confidence</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {[
                            { seq: 1, id: "cj1", name: "Crown Preparation", date: "Today", priority: "required", confidence: "99%" },
                            { seq: 2, id: "cj2", name: "Crown Delivery", date: "+14 days", priority: "required", confidence: "98%" },
                            { seq: 3, id: "cj3", name: "Bite Adjustment", date: "+21 days", priority: "optional", confidence: "82%" }
                          ].map((item) => (
                            <TableRow key={item.id} className="border-slate-800/60 hover:bg-transparent text-slate-300 text-xs">
                              <TableCell className="font-semibold text-white font-mono">{item.seq}</TableCell>
                              <TableCell className="font-semibold">{item.name}</TableCell>
                              <TableCell>{item.date}</TableCell>
                              <TableCell>
                                <Badge className={item.priority === "required" ? "bg-rose-500/20 text-rose-400 border-rose-500/30 text-[10px]" : "bg-slate-800 text-slate-400 border-slate-700 text-[10px]"}>
                                  {item.priority}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <input 
                                    type="checkbox"
                                    checked={!!emrSyncs[item.id]}
                                    onChange={() => handleToggleSync(item.id, item.name)}
                                    className="rounded border-slate-800 bg-slate-950 text-emerald-500 focus:ring-emerald-500 w-4 h-4 cursor-pointer"
                                  />
                                  <span className="text-[10px] text-slate-400">
                                    {emrSyncs[item.id] ? "Synced / Sync Scheduled" : (item.priority === "required" ? "Recommend Sync" : "Admin Review")}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="text-right text-emerald-400 font-mono font-semibold">{item.confidence}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 17: AI Care Coordinator */}
                <div id="section-17" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 pb-3">
                      <CardTitle className="text-base font-bold text-white flex items-center gap-2">
                        <Activity className="w-5 h-5 text-emerald-400" />
                        Section 17 - AI Care Coordinator
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {[
                        { title: "Create Crown Delivery Appt", desc: "Crown preparation is scheduled for today. Schedule delivery 14 days out.", status: "Pending", color: "border-amber-500/20 bg-amber-500/5 text-amber-400" },
                        { title: "Prior Auth Risk Assessment", desc: "Prior auth needed for root canal D3330. Pre-op x-rays must be attached.", status: "High Priority", color: "border-rose-500/20 bg-rose-500/5 text-rose-400" },
                        { title: "Estimate Patient Responsibility", desc: "Create cost estimate for Crown (D2740) and Root Canal (D3330).", status: "Ready to Generate", color: "border-emerald-500/20 bg-emerald-500/5 text-emerald-400" },
                        { title: "Notify Patient Before Reset", desc: "Deductible is fully met for this period. Remind patient to use remaining $1,450.", status: "Recommended", color: "border-indigo-500/20 bg-indigo-500/5 text-indigo-400" },
                        { title: "Eligibility Recheck Task", desc: "Eligibility recheck created in Priorath dashboard tasks.", status: "Scheduled", color: "border-slate-800 bg-slate-900/30 text-slate-400" },
                        { title: "Schedule Recall Cleaning", desc: "No prophylaxis cleaning on record for last 6 months. Schedule cleaning.", status: "Overdue", color: "border-slate-800 bg-slate-900/30 text-slate-400" }
                      ].map((item, idx) => (
                        <div key={idx} className={`border p-4 rounded-xl flex flex-col justify-between ${item.color.split(" ")[0]} ${item.color.split(" ")[1]}`}>
                          <div>
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="text-xs font-bold text-white uppercase tracking-wider">{item.title}</h4>
                              <Badge className={`${item.color.split(" ")[2]} text-[9px] shrink-0`}>{item.status}</Badge>
                            </div>
                            <p className="text-slate-400 text-xs">{item.desc}</p>
                          </div>
                          <Button size="xs" className="mt-4 bg-slate-900 hover:bg-slate-800 border border-slate-800 text-[10px] text-white self-start" onClick={() => toast.success(`Initiated action: ${item.title}`)}>
                            Take Action
                          </Button>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>

                {/* Section 18: Prior Authorization Readiness */}
                <div id="section-18" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 pb-3">
                      <CardTitle className="text-base font-bold text-white flex items-center gap-2">
                        <Shield className="w-5 h-5 text-amber-400" />
                        Section 18 - Prior Authorization Readiness
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader className="border-slate-800 bg-slate-900/30">
                          <TableRow className="border-slate-800 hover:bg-transparent">
                            <TableHead className="text-slate-400 font-semibold">Procedure</TableHead>
                            <TableHead className="text-slate-400 font-semibold">PA Required</TableHead>
                            <TableHead className="text-slate-400 font-semibold">Documentation Required</TableHead>
                            <TableHead className="text-slate-400 font-semibold">Readiness Status</TableHead>
                            <TableHead className="text-slate-400 font-semibold text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {[
                            { code: "D2740 Crown", req: "Possible (Verify diagnosis)", doc: "Pre-op X-rays, clinical narrative", status: "Review Needed", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
                            { code: "D3330 Root Canal", req: "Required", doc: "Pre-op X-rays (Missing in system)", status: "Action Needed", color: "bg-red-500/20 text-red-400 border-red-500/30" }
                          ].map((item, idx) => (
                            <TableRow key={idx} className="border-slate-800/60 hover:bg-transparent text-slate-300 text-xs">
                              <TableCell className="font-semibold text-white font-mono">{item.code}</TableCell>
                              <TableCell>{item.req}</TableCell>
                              <TableCell className="text-slate-400 italic">{item.doc}</TableCell>
                              <TableCell><Badge className={`${item.color} text-[10px]`}>{item.status}</Badge></TableCell>
                              <TableCell className="text-right">
                                <Button size="xs" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-[10px]" onClick={() => toast.info(`Starting Prior Auth draft for ${item.code}`)}>
                                  Draft Auth
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 19: Benefits Forecast */}
                <div id="section-19" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 pb-3">
                      <CardTitle className="text-base font-bold text-white flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-emerald-400" />
                        Section 19 - Benefits Forecast
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                      <div className="bg-slate-900/40 border border-slate-800 p-3.5 rounded-xl text-center">
                        <span className="text-[10px] text-slate-400 uppercase font-bold">Current Benefit</span>
                        <p className="text-lg font-bold text-emerald-400 mt-1">$1,450.00</p>
                      </div>
                      <div className="bg-slate-900/40 border border-slate-800 p-3.5 rounded-xl text-center">
                        <span className="text-[10px] text-slate-400 uppercase font-bold">Planned Treatment</span>
                        <p className="text-lg font-bold text-rose-400 mt-1">-$1,000.00</p>
                      </div>
                      <div className="bg-slate-900/40 border border-slate-800 p-3.5 rounded-xl text-center">
                        <span className="text-[10px] text-slate-400 uppercase font-bold">Post-Treatment Bal</span>
                        <p className="text-lg font-bold text-indigo-400 mt-1">$450.00</p>
                      </div>
                      <div className="bg-slate-900/40 border border-slate-800 p-3.5 rounded-xl text-center">
                        <span className="text-[10px] text-slate-400 uppercase font-bold">Estimated Patient Spend</span>
                        <p className="text-lg font-bold text-white mt-1">$650.00</p>
                      </div>
                      <div className="bg-slate-900/40 border border-slate-800 p-3.5 rounded-xl text-center">
                        <span className="text-[10px] text-slate-400 uppercase font-bold">Deductible Status</span>
                        <p className="text-lg font-bold text-emerald-400 mt-1">Met ($50.00)</p>
                      </div>
                      <div className="bg-slate-900/40 border border-slate-800 p-3.5 rounded-xl text-center">
                        <span className="text-[10px] text-slate-400 uppercase font-bold">Reset Countdown</span>
                        <p className="text-lg font-bold text-sky-400 mt-1">177 Days</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 20: Coverage History */}
                <div id="section-20" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 pb-3">
                      <CardTitle className="text-base font-bold text-white flex items-center gap-2">
                        <Clock className="w-5 h-5 text-slate-400" />
                        Section 20 - Coverage History
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader className="border-slate-800 bg-slate-900/30">
                          <TableRow className="border-slate-800 hover:bg-transparent">
                            <TableHead className="text-slate-400 font-semibold">Verification Date</TableHead>
                            <TableHead className="text-slate-400 font-semibold">Status</TableHead>
                            <TableHead className="text-slate-400 font-semibold">Plan Name</TableHead>
                            <TableHead className="text-slate-400 font-semibold">Identified Change</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {workspaceData.coverage_history && workspaceData.coverage_history.length > 0 ? (
                            workspaceData.coverage_history.map((hist, idx) => (
                              <TableRow key={idx} className="border-slate-800/60 hover:bg-transparent text-slate-300 text-xs">
                                <TableCell className="font-semibold text-white">
                                  {new Date(hist.created_at).toLocaleDateString()}
                                </TableCell>
                                <TableCell>{getStatusBadge(hist.coverage_status)}</TableCell>
                                <TableCell>{hist.plan_name}</TableCell>
                                <TableCell className="text-slate-400 italic">
                                  {idx === 0 ? "Current Verification (No plan modifications)" : (idx === 1 ? "Deductible changed ($50.00 met)" : "Plan changed")}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <>
                              <TableRow className="border-slate-800/60 hover:bg-transparent text-slate-300 text-xs">
                                <TableCell className="font-semibold text-white">Today</TableCell>
                                <TableCell>{getStatusBadge("Active")}</TableCell>
                                <TableCell>Family Dental PPO</TableCell>
                                <TableCell className="text-slate-400 italic">Current Verification (No plan modifications)</TableCell>
                              </TableRow>
                              <TableRow className="border-slate-800/60 hover:bg-transparent text-slate-300 text-xs">
                                <TableCell className="font-semibold text-white">30 days ago</TableCell>
                                <TableCell>{getStatusBadge("Active")}</TableCell>
                                <TableCell>Family Dental PPO</TableCell>
                                <TableCell className="text-slate-400 italic">Deductible changed ($50.00 fully met)</TableCell>
                              </TableRow>
                              <TableRow className="border-slate-800/60 hover:bg-transparent text-slate-300 text-xs">
                                <TableCell className="font-semibold text-white">90 days ago</TableCell>
                                <TableCell>{getStatusBadge("Inactive")}</TableCell>
                                <TableCell>Old Indemnity Plan</TableCell>
                                <TableCell className="text-slate-400 italic">Plan changed to Anthem Dental Complete PPO</TableCell>
                              </TableRow>
                            </>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 21: AI Risk Indicators */}
                <div id="section-21" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 pb-3">
                      <CardTitle className="text-base font-bold text-white flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-rose-400" />
                        Section 21 - Claim Risk Engine Analysis
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-5 space-y-6">
                      <div className="flex flex-col md:flex-row items-center gap-6 border-b border-slate-800 pb-5">
                        <div className="relative w-28 h-28 flex items-center justify-center rounded-full bg-slate-900 border-4 border-slate-800 shadow-inner shrink-0">
                          <div className="text-center">
                            <span className="text-3xl font-extrabold text-white">{activePatient.claim_risk_score ?? 0}</span>
                            <span className="text-[10px] text-slate-500 block uppercase font-bold mt-0.5">Score</span>
                          </div>
                        </div>
                        <div className="space-y-1.5 text-center md:text-left">
                          <h4 className="text-sm font-bold text-white">
                            Risk Level Category: <span className={
                              (activePatient.claim_risk_category === "High") ? "text-rose-400 font-extrabold" :
                              (activePatient.claim_risk_category === "Medium") ? "text-amber-400 font-extrabold" :
                              "text-emerald-400 font-extrabold"
                            }>{activePatient.claim_risk_category ?? "Low"}</span>
                          </h4>
                          <p className="text-xs text-slate-400">
                            Calculated dynamically based on EMR coverage status, benefit caps, and limitations rules.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="space-y-3">
                          <h5 className="text-xs font-bold uppercase tracking-wider text-slate-400">Risk Factor Reasons</h5>
                          <ul className="space-y-2">
                            {(activePatient.claim_risk_reasons && activePatient.claim_risk_reasons.length > 0) ? (
                              activePatient.claim_risk_reasons.map((reason, rIdx) => (
                                <li key={rIdx} className="text-xs text-slate-200 bg-slate-900/60 border border-slate-800 p-2.5 rounded-lg flex items-start gap-2">
                                  <span className="text-rose-400 mt-0.5">✕</span>
                                  <span>{reason}</span>
                                </li>
                              ))
                            ) : (
                              <li className="text-xs text-slate-500 italic">No specific risk triggers identified for this patient.</li>
                            )}
                          </ul>
                        </div>
                        
                        <div className="space-y-3">
                          <h5 className="text-xs font-bold uppercase tracking-wider text-slate-400">Recommended Corrective Actions</h5>
                          <ul className="space-y-2">
                            {(activePatient.claim_risk_actions && activePatient.claim_risk_actions.length > 0) ? (
                              activePatient.claim_risk_actions.map((act, aIdx) => (
                                <li key={aIdx} className="text-xs text-slate-200 bg-emerald-500/5 border border-emerald-500/10 p-2.5 rounded-lg flex items-start gap-2">
                                  <span className="text-emerald-400 mt-0.5">✓</span>
                                  <span>{act}</span>
                                </li>
                              ))
                            ) : (
                              <li className="text-xs text-slate-500 italic">Plan claims are eligible for scheduling immediately.</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Section 22: Patient Engagement */}
                <div id="section-22" className="scroll-mt-40">
                  <Card className="border-slate-800 bg-[#0d1321]/60 shadow-md">
                    <CardHeader className="border-b border-slate-800 pb-3">
                      <CardTitle className="text-base font-bold text-white flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-sky-400" />
                        Section 22 - Patient Engagement Opportunities
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { type: "Benefits Expiring Reminder", desc: "Send reminder that $1,450.00 benefit is available and individual deductible has been met.", action: "Send Benefits Alert" },
                        { type: "Financial Estimate Available", desc: "Send cost estimate of $650.00 for the upcoming crown and root canal.", action: "Send Estimate Details" },
                        { type: "Recall Appointment Due", desc: "No dental prophylaxis cleaning has been utilized in the current benefit period.", action: "Send Recall Schedule Link" },
                        { type: "Unused Preventive Care Warning", desc: "Notify patient that preventive diagnostic evaluations are covered at 100% and unused.", action: "Send Preventive Reminder" }
                      ].map((item, idx) => (
                        <div key={idx} className="bg-slate-900/40 border border-slate-800 p-4 rounded-xl flex flex-col justify-between shadow-sm">
                          <div>
                            <h4 className="text-xs font-bold text-white mb-1 uppercase tracking-wider">{item.type}</h4>
                            <p className="text-slate-400 text-xs">{item.desc}</p>
                          </div>
                          <div className="flex gap-2 mt-4">
                            <Button size="xs" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-[10px]" onClick={() => toast.success(`SMS sent for: ${item.type}`)}>
                              Send SMS
                            </Button>
                            <Button size="xs" variant="outline" className="border-slate-800 text-[10px] text-slate-300 hover:text-white" onClick={() => toast.success(`Email sent for: ${item.type}`)}>
                              Send Email
                            </Button>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>

                {/* End of Coverage Indicator Notes */}
                <div className="border border-emerald-500/20 bg-emerald-500/5 p-5 rounded-xl text-center space-y-2 mt-6">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto animate-pulse" />
                  <h4 className="text-white font-bold text-sm">End of Coverage & Benefit Details</h4>
                  <p className="text-xs text-slate-400 max-w-lg mx-auto">
                    All standard coverage, network comparisons, deductibles, limitations, and eligibility benefit details for this member have been fully loaded and verified. No additional coverage information exists for this query.
                  </p>
                </div>

              </div>
            </div>

            {/* FLOATING COPILOT BUTTON AND PANEL */}
            <div className="fixed bottom-24 right-6 z-50 flex flex-col items-end gap-2">
              {showCopilotPanel && (
                <Card className="w-96 border-slate-800 bg-[#0d1321]/95 backdrop-blur-md shadow-2xl animate-in slide-in-from-bottom-5 duration-200">
                  <CardHeader className="border-b border-slate-800 pb-2.5 flex flex-row justify-between items-center space-y-0">
                    <CardTitle className="text-sm font-bold text-white flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-emerald-400 animate-pulse" />
                      Coverage Copilot
                    </CardTitle>
                    <Button 
                      variant="ghost" 
                      size="xs" 
                      className="text-slate-400 hover:text-white h-6 w-6 p-0"
                      onClick={() => setShowCopilotPanel(false)}
                    >
                      ✕
                    </Button>
                  </CardHeader>
                  <CardContent className="p-4 space-y-4">
                    {/* Messages scroll box */}
                    <div className="h-64 overflow-y-auto border border-slate-800 bg-slate-950/60 rounded-xl p-3 space-y-3 flex flex-col">
                      {copilotMessages.map((msg, idx) => (
                        <div key={idx} className={`max-w-[85%] rounded-lg p-2.5 text-xs ${msg.role === "user" ? "bg-indigo-600 text-white self-end text-right" : "bg-slate-900 text-slate-300 border border-slate-800 self-start text-left"}`}>
                          <p>{msg.content}</p>
                          {msg.powered_by && (
                            <span className="text-[8px] text-slate-500 block mt-1 text-right font-mono">Powered by {msg.powered_by}</span>
                          )}
                        </div>
                      ))}
                      {copilotLoading && (
                        <div className="bg-slate-900 border border-slate-800 rounded-lg p-2.5 text-xs text-slate-400 self-start animate-pulse">
                          Thinking...
                        </div>
                      )}
                    </div>

                    {/* Prompt Suggestions */}
                    <div>
                      <span className="text-[10px] text-slate-500 font-bold uppercase block mb-1">Suggested Questions:</span>
                      <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                        {[
                          "Is the member active?",
                          "Are crowns covered?",
                          "What is remaining deductible?",
                          "What is remaining annual maximum?",
                          "Does this payer require prior auth for D2740?",
                          "What changed since the last eligibility check?"
                        ].map((q) => (
                          <button
                            key={q}
                            type="button"
                            onClick={() => {
                              setCopilotInput(q);
                              toast.info(`Click Send to ask: "${q}"`);
                            }}
                            className="px-2 py-1 rounded bg-slate-900 border border-slate-800 hover:border-slate-700 text-slate-400 hover:text-slate-200 text-[9px] transition text-left"
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Chat input box */}
                    <form onSubmit={handleSendCopilotMessage} className="flex gap-2">
                      <input
                        type="text"
                        value={copilotInput}
                        onChange={(e) => setCopilotInput(e.target.value)}
                        placeholder="Ask Copilot a question..."
                        className="flex-1 px-3 py-2 text-xs bg-slate-950 border border-slate-800 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                      />
                      <Button type="submit" size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold text-xs shrink-0" disabled={copilotLoading}>
                        Send
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
              
              <Button
                onClick={() => setShowCopilotPanel(!showCopilotPanel)}
                className="w-14 h-14 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 shadow-2xl flex items-center justify-center p-0 transition-transform duration-200 hover:scale-105"
              >
                <Sparkles className="w-6 h-6 animate-pulse" />
              </Button>
            </div>

            {/* STICKY BOTTOM ACTION BAR */}
            <div className="fixed bottom-0 left-0 lg:left-64 right-0 z-45 bg-slate-900/90 backdrop-blur-md border-t border-slate-800 py-3.5 px-6 flex flex-wrap justify-between items-center gap-4 shadow-2xl">
              <div className="flex items-center gap-3">
                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 shrink-0">
                  {activePatient.first_name} {activePatient.last_name}
                </Badge>
                <span className="text-xs text-slate-400 hidden sm:inline truncate">Anthem Dental Complete PPO · Group: ANTDENT882</span>
              </div>
              <div className="flex flex-wrap gap-2.5">
                <Button size="sm" variant="ghost" className="border border-slate-800 text-slate-400 hover:text-white text-xs gap-1.5" onClick={() => { setActivePatient(null); setWorkspaceData(null); }}>
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to Dashboard
                </Button>
                <Button size="sm" variant="outline" className="border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10 text-xs gap-1.5" onClick={() => handleOpenRecModal(activePatient)}>
                  <Calendar className="w-3.5 h-3.5 text-indigo-400" /> Open Dental Recommendations
                </Button>
                <Button size="sm" variant="outline" className="border-slate-800 text-xs bg-slate-900 text-slate-300 hover:text-white" onClick={() => checkEligibility(activePatient)}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh Eligibility
                </Button>
                <Button size="sm" variant="outline" className="border-slate-800 text-xs bg-slate-900 text-slate-300 hover:text-white" onClick={triggerCostEstimate}>
                  <DollarSign className="w-3.5 h-3.5 mr-1.5" /> Estimate Patient Cost
                </Button>
                <Button size="sm" variant="outline" className="border-slate-800 text-xs bg-slate-900 text-slate-300 hover:text-white" onClick={triggerDownloadPDF}>
                  <FileDown className="w-3.5 h-3.5 mr-1.5" /> Download PDF
                </Button>
                <Button size="sm" variant="outline" className="border-slate-800 text-xs bg-slate-900 text-slate-300 hover:text-white" onClick={() => handleCopyText(activePatient.member_id, "Member ID")}>
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy Member ID
                </Button>
                <Button size="sm" variant="outline" className="border-slate-800 text-xs bg-slate-900 text-slate-300 hover:text-white" onClick={triggerPrint}>
                  <Printer className="w-3.5 h-3.5 mr-1.5" /> Print Benefits
                </Button>
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold text-xs" onClick={triggerExport}>
                  Export Coverage
                </Button>
              </div>
            </div>

            {/* APPOINTMENT RECOMMENDATIONS WRITE-BACK MODAL */}
            {showRecModal && (
              <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-2xl w-full p-6 space-y-6 shadow-2xl">
                  <div className="flex justify-between items-start border-b border-slate-800 pb-4">
                    <div>
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-indigo-400" />
                        Open Dental Care Recommendations
                      </h3>
                      <p className="text-xs text-slate-400 mt-1">
                        Select up to 3 candidate appointment recommendations to write back into Open Dental for {activePatient?.first_name} {activePatient?.last_name}.
                      </p>
                    </div>
                    <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white" onClick={() => setShowRecModal(false)}>✕</Button>
                  </div>

                  {loadingRecs ? (
                    <div className="py-12 flex justify-center items-center gap-2 text-slate-400 text-xs">
                      <RefreshCw className="w-4 h-4 animate-spin text-emerald-400" /> Generating care recommendations...
                    </div>
                  ) : recommendationsList.length === 0 ? (
                    <div className="py-8 text-center text-slate-400 text-xs">No candidate recommendations available.</div>
                  ) : (
                    <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                      {recommendationsList.map((rec) => {
                        const isChecked = selectedRecIds.includes(rec.recommendation_id);
                        return (
                          <div
                            key={rec.recommendation_id}
                            className={`p-4 rounded-xl border transition-all cursor-pointer ${
                              isChecked
                                ? "bg-indigo-950/30 border-indigo-500/50"
                                : "bg-slate-950 border-slate-800 opacity-60"
                            }`}
                            onClick={() => {
                              if (isChecked) {
                                setSelectedRecIds(selectedRecIds.filter(id => id !== rec.recommendation_id));
                              } else {
                                setSelectedRecIds([...selectedRecIds, rec.recommendation_id]);
                              }
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {}}
                                className="mt-1 h-4 w-4 rounded border-slate-700 bg-slate-900 text-indigo-500 focus:ring-indigo-500"
                              />
                              <div className="flex-1 space-y-1">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-bold text-white">{rec.procedure_descriptions?.join(" · ")}</span>
                                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]">
                                    Est. Ins Pay: ${rec.estimated_insurance_pay?.toFixed(2)}
                                  </Badge>
                                </div>
                                <div className="text-xs text-indigo-300 font-medium">
                                  Target Slot: {rec.recommended_date_time} (Prov #{rec.prov_num}, Op #{rec.op_num})
                                </div>
                                <div className="text-xs text-slate-400">
                                  Codes: <span className="font-mono text-slate-300">{rec.cdt_codes?.join(", ")}</span>
                                </div>
                                <div className="text-[11px] text-slate-500 italic pt-1">
                                  {rec.rationale}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-4 border-t border-slate-800">
                    <span className="text-xs text-slate-400">
                      {selectedRecIds.length} recommendation(s) selected
                    </span>
                    <div className="flex gap-2">
                      <Button variant="outline" className="border-slate-700 text-xs" onClick={() => setShowRecModal(false)}>
                        Cancel
                      </Button>
                      <Button
                        disabled={writingRecs || selectedRecIds.length === 0}
                        onClick={handleSaveToOpenDental}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-xs gap-1.5"
                      >
                        {writingRecs ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                        Save to Open Dental
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Spacer for sticky bottom bar */}
            <div className="h-16" />

            {evidenceField && (
              <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm">
                <Card className="max-w-2xl w-full border-slate-800 bg-[#0d1321]/95 text-white max-h-[85vh] flex flex-col shadow-2xl">
                  <CardHeader className="border-b border-slate-800 p-4 flex justify-between flex-row items-center">
                    <div>
                      <CardTitle className="text-base font-bold text-emerald-400">Payer Audit Evidence Source</CardTitle>
                      <p className="text-xs text-slate-400 mt-1">Field: <span className="font-semibold text-white">{evidenceField}</span></p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setEvidenceField(null)} 
                      className="text-slate-400 hover:text-white hover:bg-slate-800/60"
                    >
                      ✕
                    </Button>
                  </CardHeader>
                  <CardContent className="p-4 overflow-y-auto font-mono text-xs text-slate-300 bg-slate-950/80 rounded-b-xl max-h-[60vh]">
                    <pre className="whitespace-pre-wrap">
                      {JSON.stringify(evidenceData, null, 2)}
                    </pre>
                  </CardContent>
                  <div className="border-t border-slate-800 p-3 bg-slate-900/40 text-right">
                    <Button onClick={() => setEvidenceField(null)} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold text-xs py-1.5 px-4 rounded-lg">
                      Close Evidence Log
                    </Button>
                  </div>
                </Card>
              </div>
            )}

          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
